import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Search, 
  Calendar, 
  Building2, 
  MapPin, 
  User, 
  Eye, 
  Edit, 
  Trash2, 
  ChevronDown,
  Filter,
  Download,
  Printer,
  RefreshCw,
  FileText,
  MoreVertical,
  ArrowLeft,
  Share
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { useBackNavigation } from '@/hooks/use-back-navigation';
import { useNavigate } from 'react-router-dom';
import { CustomDatePicker } from './CustomDatePicker';
import { drawInvoicePdf, estimateInvoicePdfHeight } from '@/lib/printKohineer';
import { cn } from "@/lib/utils";
import { useSalesInvoice } from '@/context/SalesInvoiceContext';
import { fetchPDF, getBaseUrl } from '@/lib/api';
import { testApiConnection, testPdfApiConnection, checkTokenValidity } from '@/lib/api-test';
import SalesInvoice from './SalesInvoice';
import SalesInvoiceMobile from './SalesInvoiceMobile';
import PdfPreviewer from './PdfPreviewer';
import { downloadPdf } from '@/lib/pdfDownload';
import jsPDF from 'jspdf';
import { 
  listBluetoothDevices, 
  connectBluetoothDevice, 
  disconnectBluetoothDevice, 
  checkBluetoothEnabled, 
  enableBluetooth, 
  sendBluetoothData,
  formatDeviceName,
  isPrinterDevice
} from '@/lib/bluetoothPrinter';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { AppBar } from './ui/app-bar';
import { useUserPrivileges } from '@/context/UserPrivilegesContext';
import { PRIVILEGE } from '@/lib/privilegeConstants';
import { useSettings } from '@/hooks/useSettings';
import { getPrinterErrorMessage } from '@/utils/printerErrorHandler';

// Map Settings "Invoice Print Template V2" (0=2inch, 1=3inch, 2=Multi Page Modern) to preview dropdown values
const INVOICE_PRINT_TEMPLATE_TO_PREVIEW: Record<string, string> = {
  '0': '2mm-print',
  '1': '3mm-print',
  '2': 'multiple-page-modern',
};


// Conditional import for Capacitor PDF viewer (only available in native environment)
let PdfViewer: any = null;
if (typeof window !== 'undefined' && Capacitor.isNativePlatform()) {
  try {
    // @ts-ignore - Dynamic require for Capacitor plugin
    PdfViewer = require('@capacitor-community/pdf-viewer').PdfViewer;
  } catch (error) {
    console.warn('PDF viewer plugin not available:', error);
  }
}

// TypeScript declaration for window.qz
declare global {
  interface Window {
    qz: any;
  }
}

// Types
interface Company {
  Id: number;
  Name: string;
  GSTN: string;
  Mobile: string;
  Email: string;
  Active: boolean;
}

interface Location {
  ID: string;
  Name: string;
  Type: string;
  state: string;
  CompanyName: string;
  CompanyID: string;
}

interface User {
  Id: number;
  UserID: string;
}

interface InvoiceReport {
  Invoiceid: number;
  "Invoice Date": string;
  "Invoice No": number;
  "Customer Name": string;
  "Customer Company": string;
  "Customer Phone": string;
  InvoicedAmount: number;
  "Total Payment Received": number;
  "Due Amount": number;
  Type: string;
  "Total Qty Invoiced": number;
  "Total Delivered": number;
  "Total Pending": number;
  LocationId: number;
  CompanyID: number;
  eCommOrder: string;
  eCommPartner: string;
}

interface InvoiceFullDetails {
  Header: any[];
  EditableColumns: any[];
  ValueChangeColumns: any[];
  TwoFields: any[];
  status: number;
  message: string;
  data: Array<{
    Invoiceid: number;
    CustomerCode: number;
    CreatedOn: string;
    Discount: number;
    OtherCharges: number;
    InvoicedAmount: number;
    Paid: number;
    PaidOn: string;
    DueAmount: number;
    Frozen: any;
    InvoiceName: string;
    ReferredDr: string;
    cashmemo: number;
    PaidDetails: string;
    CompanyID: number;
    InvSeqNo: number;
    RepName: string;
    Tag1: string;
    Tag2: string;
    Tag3: string;
    Tag4: string;
    Location: string;
    CreatedBy: string;
    PaidbyCash: number;
    PaidbyOthers: number;
    CashDenominationReceived: number;
    walletamount: number;
    Couponamount: number;
    CreditNoteamount: number;
    Chequeamount: number;
    eCommOrder: string;
    eCommPartner: string;
    PaidbyPoints: number;
    Notes: string;
    FName: string;
    LName: string;
    Mobile1: string;
    DOB: string | null;
    AmountDue: number | null;
    Locality: string | null;
    TIN: string;
    CustState: string;
    OpeningBalance: number | null;
    Name: string;
    LandPhone: string;
    Email: string;
    GSTN: string;
    Mobile: string;
    ContactPerson: string;
    ContactPersonNumber: string;
    Address1: string;
    Address2: string;
    Address3: string;
    City: string;
    CmpState: string;
    PinZip: string;
    Country: string;
    GST: number;
    id: number;
    ldiscount: number;
    Quantity: number;
    UnitCost: number;
    TotalCost: number;
    ProductCode: number;
    SerialNumber: string | null;
    SGST: number;
    CGST: number;
    IGST: number;
    OriginalCost: number;
    Cess: number;
    deliveredQty: number;
    cgstpercent: number | null;
    sgstpercent: number | null;
    igstpercent: number | null;
    cesspercent: number | null;
    Title: string;
    CollectionName: string;
    available: number | null;
    BarCode: string;
    BrandName: string;
    CessPer: number;
    sgstper: number;
    cgstper: number;
    igstper: number;
    CollectionID: number;
    Rate: number;
    UQCName: string;
    HSNCode: string;
    offerprice: number;
    ShippingAddress: string;
    ShippingEmail: string;
    ShippingGST: string;
    ShippingName: string;
    ShippingPhone: string;
    shippingstate: string;
    emailid: string;
    Address: string;
  }>;
  TotalRecords: number;
}

interface FilterState {
  searchTerm: string;
  startDate: Date;
  endDate: Date;
  companyId: string; // 'all' or company ID
  locationId: string; // 'all' or location ID
  userName: string; // 'all' or user ID
  invoiceType: 'all' | 'Invoice' | 'Credit Note';
}

const InvoiceReportTab: React.FC = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Back button should always return to Reports tab (not Administrator or navigate(-1))
  const handleBackToReports = useCallback(() => {
    navigate('/?tab=reports');
  }, [navigate]);
  
  // State
  const [filters, setFilters] = useState<FilterState>({
    searchTerm: '',
    startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)),
    endDate: new Date(),
    companyId: '0',
    locationId: '0',
    userName: 'all',  // Keeping 'all' for userName as it wasn't part of the requirement
    invoiceType: 'all'
  });
  
  const [companies, setCompanies] = useState<Company[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [partners, setPartners] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<InvoiceReport[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [mobileDisplayCount, setMobileDisplayCount] = useState(10);
  const observerRef = useRef<HTMLDivElement>(null);
  
  // Loading states
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  // Preview state
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceReport | null>(null);
  const [previewData, setPreviewData] = useState<InvoiceFullDetails | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('multiple-page-modern');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [currentPdfDoc, setCurrentPdfDoc] = useState<jsPDF | null>(null);
  
  // Mobile detection with better Android/Capacitor detection
  const [isMobile, setIsMobile] = useState(() => {
    // Check if running in Capacitor (mobile app)
    const isCapacitor = typeof window !== 'undefined' && 
      (window as any).Capacitor && (window as any).Capacitor.isNativePlatform();
    
    // Check screen width
    const isSmallScreen = typeof window !== 'undefined' && window.innerWidth <= 768;
    
    // Check user agent for mobile devices
    const isMobileUA = typeof window !== 'undefined' && 
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    return isCapacitor || isSmallScreen || isMobileUA;
  });

  // Android detection for Bluetooth functionality
  const [isAndroid, setIsAndroid] = useState(() => {
    if (typeof window === 'undefined') return false;
    
    // Check if running in Capacitor Android app
    const isCapacitorAndroid = (window as any).Capacitor && 
      (window as any).Capacitor.isNativePlatform() && 
      /Android/i.test(navigator.userAgent);
    
    // Check user agent for Android
    const isAndroidUA = /Android/i.test(navigator.userAgent);
    
    return isCapacitorAndroid || isAndroidUA;
  });
  // Filters panel collapsed by default on all devices
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  
  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<InvoiceReport | null>(null);
  const [editInvoiceData, setEditInvoiceData] = useState<any>(null);
  const [showReturnMessage, setShowReturnMessage] = useState(false);
  
  // Sales invoice context
  const { 
    setCustomer, setProducts, setPayments, setNotes, setRepName,
    setShipToForm, setShipToSame, setIsUnknown, setInvoiceForm,
    setIsEditMode: setContextEditMode, setEditingInvoiceId, 
    setOnBackToReports, setOnUpdateSuccess,
    resetForNewInvoice,
    isEditMode: contextIsEditMode, editingInvoiceId: contextEditingInvoiceId
  } = useSalesInvoice();

  // Default preview template from Settings > Invoice Print Template
  const { salesInvoiceSettings, getSetting } = useSettings();
  const serialPrintTemplate = (getSetting('Serial Print') || '').trim();
  const showSerialInPrint = serialPrintTemplate.includes('<SerialNo>');
  const defaultPreviewTemplate = INVOICE_PRINT_TEMPLATE_TO_PREVIEW[salesInvoiceSettings.invoicePrintTemplateV2] ?? 'multiple-page-modern';
  const { hasPrivilege, fetchUserPrivileges } = useUserPrivileges();
  const canEditInvoice = hasPrivilege(PRIVILEGE.INVOICE_EDIT);
  const canDeleteInvoice = hasPrivilege(PRIVILEGE.INVOICE_DELETE);
  const showAllBranches = hasPrivilege(PRIVILEGE.ALL_BRANCHES);

  // Refetch privileges when invoice report screen mounts so edit/delete buttons stay visible (avoids disappearing after navigation)
  useEffect(() => {
    fetchUserPrivileges();
  }, [fetchUserPrivileges]);

  // When user does not have "All Branches" privilege, default company/location/user to first option instead of "All"
  useEffect(() => {
    if (showAllBranches) return;
    const needCompany = companies.length > 0 && filters.companyId === '0';
    const needLocation = locations.length > 0 && filters.locationId === '0';
    const needUser = users.length > 0 && filters.userName === 'all';
    if (!needCompany && !needLocation && !needUser) return;
    setFilters((prev) => {
      const next = { ...prev };
      if (needCompany) next.companyId = String(companies[0].Id);
      if (needLocation) next.locationId = locations[0].ID;
      if (needUser) next.userName = users[0].UserID;
      return next;
    });
  }, [showAllBranches, companies, locations, users, filters.companyId, filters.locationId, filters.userName]);

  // API base URL and token
  const baseUrl = getBaseUrl();
  const token = localStorage.getItem('token') || '';
  const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  
  // Debug token and base URL
  useEffect(() => {
    console.log('Debug - Base URL:', baseUrl);
    console.log('Debug - Token:', token ? 'Token exists' : 'No token');
    console.log('Debug - Auth Token:', authToken);
  }, [baseUrl, token, authToken]);

  // Debug device detection
  useEffect(() => {
    console.log('Debug - Device Detection:');
    console.log('User Agent:', navigator.userAgent);
    console.log('Is Mobile:', isMobile);
    console.log('Is Android:', isAndroid);
    console.log('Screen Width:', window.innerWidth);
    console.log('Capacitor Available:', typeof window !== 'undefined' && (window as any).Capacitor);
    console.log('Capacitor Native Platform:', typeof window !== 'undefined' && (window as any).Capacitor && (window as any).Capacitor.isNativePlatform());
    console.log('Bluetooth Available:', typeof window !== 'undefined' && window.bluetoothSerial);
  }, [isMobile, isAndroid]);
  
  // Mobile detection effect
  useEffect(() => {
    const handleResize = () => {
      // Check if running in Capacitor (mobile app)
      const isCapacitor = typeof window !== 'undefined' && 
        (window as any).Capacitor && (window as any).Capacitor.isNativePlatform();
      
      // Check screen width
      const isSmallScreen = window.innerWidth <= 768;
      
      // Check user agent for mobile devices
      const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      const newIsMobile = isCapacitor || isSmallScreen || isMobileUA;
      setIsMobile(newIsMobile);
      // Keep collapsed by default on all devices; do not auto-expand
      
      // Update Android detection
      const newIsAndroid = (isCapacitor && /Android/i.test(navigator.userAgent)) || /Android/i.test(navigator.userAgent);
      setIsAndroid(newIsAndroid);
    };
    
    // Call handler immediately on mount
    handleResize();
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // API functions
  const fetchCompanies = useCallback(async () => {
    setLoadingCompanies(true);
    try {
      console.log('Fetching companies from:', `${baseUrl}/api/Companies`);
      console.log('Using auth token:', authToken);
      
      const response = await fetch(`${baseUrl}/api/Companies`, {
        method: 'GET',
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });
      
      console.log('Companies response status:', response.status);
      console.log('Companies response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Companies API error response:', errorText);
        throw new Error(`Failed to fetch companies: ${response.status} ${response.statusText} - ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Companies API response:', data);
      
      // Handle different response structures
      if (data.status === 0 && data.message === 'Success') {
        const companiesData = data.data || data.Data || [];
        setCompanies(companiesData);
        console.log('Companies loaded successfully:', companiesData.length);
      } else if (Array.isArray(data)) {
        // Direct array response
        setCompanies(data);
        console.log('Companies loaded successfully (direct array):', data.length);
      } else {
        console.warn('Companies API returned unexpected format:', data);
        setCompanies([]);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast({
        title: "Connection Error",
        description: "Unable to load companies. Please check your internet connection and try again.",
        variant: "destructive",
      });
    } finally {
      setLoadingCompanies(false);
    }
  }, [baseUrl, authToken, toast]);
  
  const fetchLocations = useCallback(async () => {
    setLoadingLocations(true);
    try {
      console.log('Fetching locations from:', `${baseUrl}/api/Locations`);
      
      const response = await fetch(`${baseUrl}/api/Locations`, {
        method: 'GET',
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });
      
      console.log('Locations response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Locations API error response:', errorText);
        throw new Error(`Failed to fetch locations: ${response.status} ${response.statusText} - ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Locations API response:', data);
      
      // Handle different response structures
      if (data.status === 0 && data.message === 'Success') {
        const locationsData = data.data || data.Data || [];
        setLocations(locationsData);
        console.log('Locations loaded successfully:', locationsData.length);
      } else if (Array.isArray(data)) {
        // Direct array response
        setLocations(data);
        console.log('Locations loaded successfully (direct array):', data.length);
      } else {
        console.warn('Locations API returned unexpected format:', data);
        setLocations([]);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      toast({
        title: "Connection Error",
        description: "Unable to load locations. Please check your internet connection and try again.",
        variant: "destructive",
      });
    } finally {
      setLoadingLocations(false);
    }
  }, [baseUrl, authToken, toast]);
  
  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      console.log('Fetching users from:', `${baseUrl}/api/Users`);
      
      const response = await fetch(`${baseUrl}/api/Users`, {
        method: 'GET',
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });
      
      console.log('Users response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Users API error response:', errorText);
        throw new Error(`Failed to fetch users: ${response.status} ${response.statusText} - ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Users API response:', data);
      
      // Handle different response structures
      if (data.status === 0 && data.message === 'Success') {
        const usersData = data.data || data.Data || [];
        setUsers(usersData);
        console.log('Users loaded successfully:', usersData.length);
      } else if (Array.isArray(data)) {
        // Direct array response
        setUsers(data);
        console.log('Users loaded successfully (direct array):', data.length);
      } else {
        console.warn('Users API returned unexpected format:', data);
        setUsers([]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: "Connection Error",
        description: "Unable to load users. Please check your internet connection and try again.",
        variant: "destructive",
      });
    } finally {
      setLoadingUsers(false);
    }
  }, [baseUrl, authToken, toast]);

  const fetchPartners = useCallback(async () => {
    setLoadingPartners(true);
    try {
      console.log('Fetching partners from:', `${baseUrl}/api/Partners`);
      
      const response = await fetch(`${baseUrl}/api/Partners`, {
        method: 'GET',
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });
      
      console.log('Partners response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Partners API error response:', errorText);
        throw new Error(`Failed to fetch partners: ${response.status} ${response.statusText} - ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Partners API response:', data);
      
      // Handle different response structures
      if (data.status === 0 && data.message === 'Success') {
        const partnersData = data.data || data.Data || [];
        setPartners(partnersData);
        console.log('Partners loaded successfully:', partnersData.length);
      } else if (Array.isArray(data)) {
        // Direct array response
        setPartners(data);
        console.log('Partners loaded successfully (direct array):', data.length);
      } else {
        console.warn('Partners API returned unexpected format:', data);
        setPartners([]);
      }
    } catch (error) {
      console.error('Error fetching partners:', error);
      toast({
        title: "Connection Error",
        description: "Unable to load partners. Please check your internet connection and try again.",
        variant: "destructive",
      });
    } finally {
      setLoadingPartners(false);
    }
  }, [baseUrl, authToken, toast]);
  
  const fetchInvoiceReport = useCallback(async () => {
    setLoadingInvoices(true);
    try {
      // Ensure dates are in YYYY-MM-DD format using local date (not UTC)
      const startDateStr = filters.startDate.getFullYear() + '-' + 
        String(filters.startDate.getMonth() + 1).padStart(2, '0') + '-' + 
        String(filters.startDate.getDate()).padStart(2, '0');
      const endDateStr = filters.endDate.getFullYear() + '-' + 
        String(filters.endDate.getMonth() + 1).padStart(2, '0') + '-' + 
        String(filters.endDate.getDate()).padStart(2, '0');
      
      // Build query parameters exactly matching the working Postman request
      const params = new URLSearchParams({
        StartDate: startDateStr,
        EndDate: endDateStr,
        LocationID: filters.locationId,
        CompanyID: filters.companyId,
        username: filters.userName  // Always send the username value as is
      });
      
      const apiEndpoint = `${baseUrl}/api/InvoiceReportCLWise?${params.toString()}`;
      console.log('=== INVOICE REPORT API CALL ===');
      console.log('API Endpoint:', apiEndpoint);
      console.log('Base URL:', baseUrl);
      console.log('Query Parameters:', {
        StartDate: startDateStr,
        EndDate: endDateStr,
        LocationID: filters.locationId,
        CompanyID: filters.companyId,
        username: filters.userName
      });
      console.log('================================');
      
      console.log('Debug - API Request Parameters:', {
        StartDate: startDateStr,
        EndDate: endDateStr,
        LocationID: filters.locationId,
        CompanyID: filters.companyId,
        username: filters.userName
      });
      
      const response = await fetch(apiEndpoint, {
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch invoice report: ${response.status} ${response.statusText} - ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Debug - API Response:', data);
      
      // Handle successful response
      if (data.status === 0 && data.message === 'Success') {
        const invoices = data.data || [];
        console.log('Debug - Found invoices:', invoices.length);
        setInvoices(invoices);
        setTotalRecords(data.TotalRecords || 0);
      } else {
        // Handle API error response
        console.warn('API returned error status:', {
          status: data.status,
          message: data.message,
          data: data.data,
          totalRecords: data.TotalRecords
        });
        setInvoices([]);
        setTotalRecords(0);
        
        // Show user-friendly error message
        toast({
          title: "No Data Available",
          description: "No invoice data found for the selected filters. Try adjusting your search criteria.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error fetching invoice report:', error);
              toast({
          title: "Connection Error",
          description: "Unable to load invoice data. Please check your internet connection and try again.",
          variant: "destructive",
        });
    } finally {
      setLoadingInvoices(false);
    }
  }, [filters, baseUrl, authToken, toast]);
  
  const fetchInvoiceDetails = useCallback(async (invoiceId: number) => {
    setLoadingPreview(true);
    try {
      const apiEndpoint = `${baseUrl}/api/InvoiceFullDetails?InvoiceID=${invoiceId}`;
      console.log('=== INVOICE FULL DETAILS API CALL ===');
      console.log('API Endpoint:', apiEndpoint);
      console.log('InvoiceID:', invoiceId);
      console.log('Base URL:', baseUrl);
      console.log('=====================================');
      
      const response = await fetch(apiEndpoint, {
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) throw new Error('Failed to fetch invoice details');
      
      const data = await response.json();
      setPreviewData(data);
      
      // Preview will be automatically generated by the useEffect when data is set
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      toast({
        title: "Error",
        description: "Failed to load invoice details",
        variant: "destructive",
      });
    } finally {
      setLoadingPreview(false);
    }
  }, [baseUrl, authToken, toast]);

  const fetchInvoiceDetailsForEdit = useCallback(async (invoiceId: number) => {
    setLoadingPreview(true);
    try {
      const apiEndpoint = `${baseUrl}/api/InvoiceFullDetails?InvoiceID=${invoiceId}`;
      console.log('=== INVOICE FULL DETAILS FOR EDIT API CALL ===');
      console.log('API Endpoint:', apiEndpoint);
      console.log('InvoiceID:', invoiceId);
      console.log('Base URL:', baseUrl);
      console.log('==============================================');
      
      console.log('Fetching invoice details for edit, InvoiceID:', invoiceId);
      
      const response = await fetch(apiEndpoint, {
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch invoice details: ${response.status} ${response.statusText} - ${errorText}`);
      }
      
      const result = await response.json();
      console.log('=== INVOICE DETAILS API RESPONSE ===');
      console.log('Response status:', result.status);
      console.log('Response message:', result.message);
      console.log('Response data length:', result.data?.length || 0);
      console.log('Response data:', result.data);
      console.log('====================================');
      
      if (result.status === 0 && result.message === 'Success' && result.data) {
        // Check if we have data, even if it's empty
        if (result.data.length === 0) {
          console.log('=== EMPTY DATA RESPONSE ===');
          console.log('Status:', result.status);
          console.log('Message:', result.message);
          console.log('Data exists:', !!result.data);
          console.log('Data length:', result.data?.length || 0);
          console.log('This invoice has no product data, creating empty invoice for editing');
          console.log('================================');
          
          // Create a minimal invoice structure for editing
          const emptyInvoiceData = {
            ...result,
            data: [{
              Invoiceid: invoiceId,
              // Add minimal required fields
              FName: '',
              LName: '',
              Mobile1: '',
              Title: '',
              Quantity: 0,
              Rate: 0,
              TotalCost: 0,
              // Add other required fields with default values
              CustomerCode: 0,
              CreatedOn: new Date().toISOString(),
              Discount: 0,
              OtherCharges: 0,
              InvoicedAmount: 0,
              Paid: 0,
              PaidOn: '',
              DueAmount: 0,
              Frozen: false,
              InvoiceName: '',
              ReferredDr: '',
              cashmemo: 0,
              PaidDetails: '',
              CompanyID: 0,
              InvSeqNo: 0,
              RepName: '',
              Tag1: '',
              Tag2: '',
              Tag3: '',
              Tag4: '',
              Location: '',
              CreatedBy: '',
              PaidbyCash: 0,
              PaidbyOthers: 0,
              CashDenominationReceived: 0,
              walletamount: 0,
              Couponamount: 0,
              CreditNoteamount: 0,
              Chequeamount: 0,
              eCommOrder: '',
              eCommPartner: '',
              PaidbyPoints: 0,
              Notes: '',
              DOB: null,
              AmountDue: null,
              Locality: null,
              TIN: '',
              CustState: '',
              OpeningBalance: null,
              Name: '',
              LandPhone: '',
              Email: '',
              GSTN: '',
              Mobile: '',
              ContactPerson: '',
              ContactPersonNumber: '',
              Address1: '',
              Address2: '',
              Address3: '',
              City: '',
              CmpState: '',
              PinZip: '',
              Country: '',
              GST: 0,
              id: 0,
              ldiscount: 0,
              UnitCost: 0,
              ProductCode: 0,
              SerialNumber: null,
              SGST: 0,
              CGST: 0,
              IGST: 0,
              OriginalCost: 0,
              Cess: 0,
              deliveredQty: 0,
              cgstpercent: null,
              sgstpercent: null,
              igstpercent: null,
              cesspercent: null,
              CollectionName: '',
              available: null,
              BarCode: '',
              BrandName: '',
              CessPer: 0,
              sgstper: 0,
              cgstper: 0,
              igstper: 0,
              CollectionID: 0,
              UQCName: '',
              HSNCode: '',
              offerprice: 0
            }]
          };
          
          // Store the empty invoice data for editing
          setEditInvoiceData(emptyInvoiceData);
          
          // Set context edit mode and callbacks
          setContextEditMode(true);
          setEditingInvoiceId(invoiceId);
          setOnBackToReports(() => handleBackFromEdit);
          setOnUpdateSuccess(() => handleInvoiceUpdateSuccess);
          
          console.log('=== PROCESSING EMPTY INVOICE DATA ===');
          console.log('Created empty invoice structure for editing');
          console.log('==========================================');
          
          // Populate context with empty data
          try {
            populateSalesInvoiceContextWithMultipleProducts(emptyInvoiceData.data);
          } catch (error) {
            console.error('Error populating empty invoice context:', error);
            toast({
              title: "Warning",
              description: "Could not set up empty invoice properly, but you can still create a new invoice",
              variant: "destructive",
            });
          }
          
          return emptyInvoiceData;
        }
        
        // Store the full invoice data for editing
        setEditInvoiceData(result);
        
        // Set context edit mode and callbacks
        setContextEditMode(true);
        setEditingInvoiceId(result.data[0].Invoiceid);
        setOnBackToReports(() => handleBackFromEdit);
        setOnUpdateSuccess(() => handleInvoiceUpdateSuccess);
        
        // Get the first record for basic invoice info
        const invoiceData = result.data[0];
        
        console.log('=== PROCESSING INVOICE DATA ===');
        console.log('Data length:', result.data.length);
        console.log('First record:', invoiceData);
        console.log('Will use multiple products function:', result.data.length > 1);
        console.log('All records:', result.data);
        console.log('================================');
        
        // Always use the multiple products function since we want to handle both single and multiple products
        console.log('Calling populateSalesInvoiceContextWithMultipleProducts');
        try {
          // Prepare listener to clean up the blob URL after printing completes in child window
          try {
            const blobUrlForCleanup = pdfUrl;
            const handleAfterPrintMessage = (event: MessageEvent) => {
              if (event && event.data && event.data.type === 'invoice-report-afterprint') {
                try { URL.revokeObjectURL(blobUrlForCleanup); } catch {}
                window.removeEventListener('message', handleAfterPrintMessage as any);
              }
            };
            window.addEventListener('message', handleAfterPrintMessage as any, { once: true } as any);
          } catch {}
          populateSalesInvoiceContextWithMultipleProducts(result.data);
        } catch (error) {
          console.error('Error populating sales invoice context:', error);
          toast({
            title: "Warning",
            description: "Some invoice data could not be loaded properly, but you can still edit the invoice",
            variant: "destructive",
          });
        }
        
        return result;
      } else {
        console.log('=== INVALID RESPONSE FORMAT ===');
        console.log('Status:', result.status);
        console.log('Message:', result.message);
        console.log('Data exists:', !!result.data);
        console.log('Data length:', result.data?.length || 0);
        console.log('===============================');
        throw new Error('Invalid response format or no data found');
      }
    } catch (error) {
      console.error('Error fetching invoice details for edit:', error);
      toast({
        title: "Error",
        description: "Failed to load invoice details for editing",
        variant: "destructive",
      });
      return null;
    } finally {
      setLoadingPreview(false);
    }
  }, [baseUrl, authToken, toast, partners, locations]);

  const populateSalesInvoiceContextWithMultipleProducts = useCallback((invoiceDataArray: any[]) => {
    if (!Array.isArray(invoiceDataArray)) {
      console.error('Invalid invoice data array - not an array');
      return;
    }
    
    console.log('=== POPULATING SALES INVOICE CONTEXT WITH MULTIPLE PRODUCTS ===');
    console.log('Invoice data array length:', invoiceDataArray.length);
    console.log('Invoice data array:', invoiceDataArray);
    console.log('Available partners:', partners);
    console.log('Available locations:', locations);
    console.log('==============================================================');
    
    // Handle empty array case - set up empty invoice context
    if (invoiceDataArray.length === 0) {
      console.log('Empty invoice data array - setting up empty invoice context');
      
      // Set empty customer data
      setCustomer({
        Phone: '',
        Mobile1: '',
        Name: '',
        Company: '',
        GST: '',
        GSTN: '',
        Email: '',
        Address: '',
        Address1: '',
        Address2: '',
        Address3: '',
        City: '',
        State: '',
        PinZip: '',
        Country: '',
        ReferredBy: '',
        SelectCompany: '',
        Location: '',
        Partner: '',
        eCommOrderID: '',
        ContactPerson: '',
        ContactPersonNumber: '',
        LandPhone: '',
        DOB: '',
        Locality: '',
        OpeningBalance: '',
      });
      
      // Set empty product items
      setProducts([]);
      
      console.log('Empty invoice context set up successfully');
      return;
    }
    
    // Use the first record for customer data
    const firstRecord = invoiceDataArray[0];
    
    // Debug: Log all available fields in the first record
    console.log('First record fields:', Object.keys(firstRecord));
    console.log('First record data:', firstRecord);
    
    // Map eCommPartner to Partner ID (eCommPartner contains the partner dropdown value)
    let partnerId = '';
    if (firstRecord.eCommPartner && firstRecord.eCommPartner !== "0" && partners.length > 0) {
      const matchingPartner = partners.find(p => p.PartnerName === firstRecord.eCommPartner);
      if (matchingPartner) {
        partnerId = matchingPartner.Id.toString();
      }
    }
    
    // Map location name to location ID
    let locationId = '';
    if (firstRecord.Location && locations.length > 0) {
      const matchingLocation = locations.find(l => l.Name === firstRecord.Location);
      if (matchingLocation) {
        locationId = matchingLocation.ID;
      }
    }
    
    // Detect if this is an Unknown customer
    const isUnknownCustomer = 
      (firstRecord.FName && firstRecord.FName.toUpperCase() === 'UNKNOWN') ||
      (firstRecord.LName && firstRecord.LName.toUpperCase() === 'UNKNOWN') ||
      (firstRecord.Name && firstRecord.Name.toUpperCase().includes('UNKNOWN'));
    
    console.log('=== UNKNOWN CUSTOMER DETECTION ===');
    console.log('FName:', firstRecord.FName);
    console.log('LName:', firstRecord.LName);
    console.log('Name:', firstRecord.Name);
    console.log('Is Unknown Customer:', isUnknownCustomer);
    console.log('================================');
    
    // Set Unknown checkbox state
    setIsUnknown(isUnknownCustomer);
    
    // Set customer data with all available fields from API response
    // For Unknown customers, clear all fields except Name/Company and make them read-only
    // Convert CreatedOn to YYYY-MM-DD format for InvoiceDate
    const invoiceDate = firstRecord.CreatedOn 
      ? new Date(firstRecord.CreatedOn).toISOString().slice(0, 10)
      : '';
    
    // Resolve Location to the dropdown/API id when API provides a name (e.g. "Hyderabad")
    const rawLocation = (invoiceData?.Location ?? '') as any;
    const rawLocationStr = typeof rawLocation === 'string' ? rawLocation.trim() : '';
    const resolvedLocationId =
      rawLocationStr && locations?.length
        ? (locations.find(l => String(l?.Name ?? '').trim().toLowerCase() === rawLocationStr.toLowerCase())?.ID ?? '')
        : '';

    if (isUnknownCustomer) {
      // For Unknown customers, only set Name/Company to UNKNOWN and clear all other fields
      console.log('=== CLEARING UNKNOWN CUSTOMER FIELDS ===');
      console.log('Clearing phone, address, email, etc. for Unknown customer');
      setCustomer({
        Phone: '',
        Mobile1: '',
        Name: 'UNKNOWN',
        Company: 'UNKNOWN',
        GST: '',
        GSTN: '',
        Email: '',
        Address: '',
        Address1: '',
        Address2: '',
        Address3: '',
        City: '',
        State: firstRecord.CustState || firstRecord.CmpState || '', // Keep state for business logic
        PinZip: '',
        Country: '',
        ReferredBy: '',
        SelectCompany: firstRecord.CompanyID?.toString() || '',
        Location: locationId,
        Partner: partnerId,
        eCommOrderID: '', // Clear eComm fields for Unknown
        ContactPerson: '',
        ContactPersonNumber: '',
        LandPhone: '',
        DOB: '',
        Locality: '',
        OpeningBalance: '',
        InvoiceDate: invoiceDate,
      });
      console.log('=== UNKNOWN CUSTOMER FIELDS CLEARED ===');
    } else {
      // For regular customers, populate all fields normally
      setCustomer({
        Phone: firstRecord.Mobile1 || firstRecord.Mobile || '',
        Mobile1: firstRecord.Mobile1 || firstRecord.Mobile || '',
        Name: firstRecord.FName || '',
        Company: firstRecord.LName || '',
        GST: firstRecord.TIN || firstRecord.GSTN || '',
        GSTN: firstRecord.TIN || firstRecord.GSTN || '',
        Email: firstRecord['Customer Email'] ?? firstRecord.emailid ?? firstRecord.Email ?? '',
        Address: firstRecord.Address || '',
        Address1: firstRecord.Address1 || '',
        Address2: firstRecord.Address2 || '',
        Address3: firstRecord.Address3 || '',
        City: firstRecord.City || '',
        State: firstRecord.CustState || firstRecord.CmpState || '',
        PinZip: firstRecord.PinZip || '',
        Country: firstRecord.Country || '',
        ReferredBy: firstRecord.ReferredDr || '',
        SelectCompany: firstRecord.CompanyID?.toString() || '',
        Location: locationId,
        Partner: partnerId,
        eCommOrderID: firstRecord.eCommOrder || '', // eCommOrder contains the eComm# textbox value
        ContactPerson: firstRecord.ContactPerson || '',
        ContactPersonNumber: firstRecord.ContactPersonNumber || '',
        LandPhone: firstRecord.LandPhone || '',
        DOB: firstRecord.DOB || '',
        Locality: firstRecord.Locality || '',
        OpeningBalance: firstRecord.OpeningBalance?.toString() || '',
        InvoiceDate: invoiceDate, // CreatedOn converted to date string
      });
    }

    console.log('=== PREPARING PRODUCT ITEMS FOR CONTEXT ===');
    console.log('Number of products to map:', invoiceDataArray.length);
    // isIGST from API: 0 = intrastate (GST = SGST+CGST), 1 = interstate (GST = IGST only). Fallback to CustState !== CmpState.
    const isIGST = firstRecord.isIGST === 1 || firstRecord.isIGST === true
      || !!(firstRecord.CustState && firstRecord.CmpState && firstRecord.CustState !== firstRecord.CmpState);

    // Reverse order so edit screen matches post order (first posted = first shown), same as desktop
    const lineItemsInPostOrder = [...invoiceDataArray].reverse();
    // Convert all records to product items with all available fields
    const productItems = lineItemsInPostOrder.map((record, index) => {
      console.log(`Mapping product ${index + 1}:`, record.Title);
      
      // OfferPrice: only from JSON (offerprice / OfferPrice). No default/derived value when not present.
      const total = record.TotalCost || 0;
      const qty = record.Quantity || 1;
      const rawOfferPrice = ('offerprice' in record) ? record.offerprice : (('OfferPrice' in record) ? record.OfferPrice : undefined);
      const parsed = (rawOfferPrice !== undefined && rawOfferPrice !== null && rawOfferPrice !== '') ? parseFloat(rawOfferPrice) : NaN;
      const offerPrice = Number.isNaN(parsed) ? undefined : Math.round(parsed);

      const discount = record.ldiscount || 0;

      // MRP: only from api/InvoiceFullDetails JSON — no formula, no fallback
      const mrpFromApi = (record.MRP !== undefined && record.MRP !== null && record.MRP !== '')
        ? parseFloat(record.MRP)
        : 0;
      const mrp = Number.isNaN(mrpFromApi) ? 0 : mrpFromApi;

      const offerPriceForCalc = offerPrice ?? 0; // use 0 only for calculations when API did not send offerprice
      const calculatedDiscountPer = discount > 0 && mrp > 0 ? (100 * (mrp - offerPriceForCalc) / mrp) : 0;

      // Use costprice from API (InvoiceFullDetails) - correct value; UnitCost may be taxable/unit price
      const costPrice = record.costprice ?? record.CostPrice ?? record.UnitCost ?? record.OriginalCost ?? 0;

      // GST/Cess: same formulas as POST so edit table and POST match (GSTAmount from API total; SGST/CGST same value when intrastate)
      const sgstAmt = parseFloat(String(record.SGST ?? 0));
      const cgstAmt = parseFloat(String(record.CGST ?? 0));
      const igstAmt = parseFloat(String(record.IGST ?? 0));
      const cessAmt = parseFloat(String(record.Cess ?? 0));
      const gstAmountFromApi = isIGST ? igstAmt : (sgstAmt + cgstAmt);
      const cessAmountFromApi = cessAmt;
      let sgstVal = 0, cgstVal = 0;
      if (!isIGST && gstAmountFromApi > 0) {
        const half = Math.floor((gstAmountFromApi / 2) * 100) / 100;
        sgstVal = half;
        cgstVal = half;
      }

      const item = {
      ProdCode: record.ProductCode || '',
      code: record.ProductCode || '',
      Title: record.Title || '',
      Barcode: record.BarCode || '',
      HSN: record.HSNCode || '',
      MRP: mrp,
      Qty: record.Quantity || 0,
      DelQty: record.deliveredQty ?? record.Quantity ?? 0,
      Discount: record.ldiscount || 0,
      DiscountPer: calculatedDiscountPer.toFixed(2),
      SGST: sgstVal,
      CGST: cgstVal,
      IGST: record.IGST || 0,
      Cess: record.Cess || 0,
      CessAmount: cessAmountFromApi,
      Total: record.TotalCost || 0,
      UnitCost: record.UnitCost || 0,
      UnitPrice: record.UnitCost || record.Rate || 0,
      OriginalCost: record.OriginalCost || 0,
      CostPrice: costPrice,
      OriginalCostPrice: costPrice,
      available: record.available || 0,
      Available: record.available || 0,
      CollectionName: record.CollectionName || '',
      BrandName: record.BrandName || '',
      UQCName: record.UQCName || '',
      deliveredQty: record.deliveredQty ?? record.Quantity ?? 0,
      GST: (record.cgstper || 0) + (record.sgstper || 0) || (record.igstper || 0), // Use total GST percentage (CGST+SGST for intra-state, IGST for inter-state)
      OfferPrice: offerPrice, // Use API offerprice/OfferPrice when present, else derived
      GSTAmount: gstAmountFromApi.toFixed(2),
      CessPer: record.cesspercent ?? record.CessPer ?? 0,
      sgstper: record.sgstper || 0,
      cgstper: record.cgstper || 0,
      igstper: record.igstper || 0,
      CollectionID: record.CollectionID || 0,
      SerialNumber: record.SerialNumber || '',
      sgstpercent: record.sgstpercent || 0,
      cgstpercent: record.cgstpercent || 0,
      igstpercent: record.igstpercent || 0,
      cesspercent: record.cesspercent || 0,
    };
      console.log(`Product ${index + 1} mapped:`, item);
      return item;
    });
    
    console.log('=== SETTING PRODUCTS IN CONTEXT ===');
    console.log('Number of products to set:', productItems.length);
    console.log('Product items to set:', productItems);
    
    // Set products in context
    setProducts(productItems);
    
    // Log confirmation
    console.log('=== PRODUCTS SET IN CONTEXT ===');
    console.log('Products have been set in context');

    // Set payment data with all available fields
    // Fix date mapping: PaidOn is actually due date, not payment date
    // Fix timezone issue by parsing the date as UTC and formatting properly
    let dueDate = '';
    if (firstRecord.PaidOn && firstRecord.PaidOn !== '0001-01-01T00:00:00') {
      // Parse the date string directly without timezone conversion
      const datePart = firstRecord.PaidOn.split('T')[0]; // Get just the date part (YYYY-MM-DD)
      dueDate = datePart;
    }
    
    console.log('=== DATE MAPPING DEBUG ===');
    console.log('CreatedOn (Invoice Date):', firstRecord.CreatedOn);
    console.log('PaidOn (Due Date):', firstRecord.PaidOn);
    console.log('Processed Due Date:', dueDate);
    console.log('Invoice Date for customer:', invoiceDate);
    console.log('==========================');
    
    // Parse Payments list from API
    let parsedPayments = [];
    if (firstRecord.Payments) {
      try {
        parsedPayments = JSON.parse(firstRecord.Payments);
        console.log('=== PARSED PAYMENTS FROM API ===');
        console.log('Payments JSON string:', firstRecord.Payments);
        console.log('Parsed payments array:', parsedPayments);
        console.log('================================');
      } catch (error) {
        console.error('Error parsing Payments JSON:', error);
        parsedPayments = [];
      }
    }
    
    // Initialize payment fields with default values
    let paymentData = {
      splitPaymentEnabled: false,
      selectedPaymentMethod: 'Cash',
      cashPayment: '',
      walletProvider: 'PhonePe',
      walletAmount: '',
      walletTxNo: '',
      chequeAmount: '',
      chequeNo: '',
      couponNo: '',
      couponAmount: '0',
      creditNoteNo: '',
      creditNoteAmount: '0',
      pointsRedeem: '',
      bankTransAmount: '',
      cardPayment: '',
      payBefore: dueDate,
      cashDenominationReceived: firstRecord.CashDenominationReceived?.toString() || '',
    };
    
    // Map payments from API to UI fields according to mapping rules
    if (parsedPayments.length > 0) {
      console.log('=== MAPPING PAYMENTS FROM API TO UI ===');
      
      // If there are multiple payments, enable split payment
      if (parsedPayments.length > 1) {
        paymentData.splitPaymentEnabled = true;
        console.log('Multiple payments detected - enabling split payment');
      }
      
      parsedPayments.forEach((payment, index) => {
        console.log(`Processing payment ${index + 1}:`, payment);
        
        const paymentMode = payment.PaymentMode || '';
        const amount = parseFloat(payment.amount) || 0;
        const notes = payment.Notes || '';
        const transactionNo = payment.TransactionNo || '';
        const bank = payment.Bank || '';
        const couponCode = payment.CouponCode || '';
        const creditNoteNo = payment.CreditNoteNo || '';
        
        console.log(`Payment ${index + 1} details:`, {
          paymentMode,
          amount,
          notes,
          transactionNo,
          bank,
          couponCode,
          creditNoteNo
        });
        
        // Map according to field mapping rules
        switch (paymentMode.toLowerCase()) {
          case 'cash':
            paymentData.cashPayment = amount.toString();
            // Only set selectedPaymentMethod if this is the first payment or if split payment is disabled
            if (index === 0 || !paymentData.splitPaymentEnabled) {
              paymentData.selectedPaymentMethod = 'Cash';
            }
            break;
          case 'card':
            paymentData.cardPayment = amount.toString();
            if (index === 0 || !paymentData.splitPaymentEnabled) {
              paymentData.selectedPaymentMethod = 'Card';
            }
            break;
          case 'wallet':
            paymentData.walletAmount = amount.toString();
            paymentData.walletProvider = notes || 'PhonePe'; // Use Notes as Wallet Provider
            paymentData.walletTxNo = transactionNo || ''; // Use TransactionNo as UPI Ref
            if (index === 0 || !paymentData.splitPaymentEnabled) {
              paymentData.selectedPaymentMethod = 'Wallets';
            }
            break;
          case 'cheque':
            paymentData.chequeAmount = amount.toString();
            paymentData.chequeNo = notes || ''; // Use Notes as Cheque No
            if (index === 0 || !paymentData.splitPaymentEnabled) {
              paymentData.selectedPaymentMethod = 'Cheque';
            }
            break;
          case 'coupon':
            paymentData.couponAmount = amount.toString();
            paymentData.couponNo = couponCode || ''; // Use CouponCode field
            if (index === 0 || !paymentData.splitPaymentEnabled) {
              paymentData.selectedPaymentMethod = 'Coupon';
            }
            break;
          case 'credit note':
            paymentData.creditNoteAmount = amount.toString();
            paymentData.creditNoteNo = creditNoteNo || ''; // Use CreditNoteNo field
            if (index === 0 || !paymentData.splitPaymentEnabled) {
              paymentData.selectedPaymentMethod = 'Credit Note';
            }
            break;
          case 'points':
            paymentData.pointsRedeem = amount.toString();
            if (index === 0 || !paymentData.splitPaymentEnabled) {
              paymentData.selectedPaymentMethod = 'Points';
            }
            break;
          default:
            console.log(`Unknown payment mode: ${paymentMode}`);
            break;
        }
      });
      
      console.log('=== FINAL PAYMENT DATA ===');
      console.log('Mapped payment data:', paymentData);
      console.log('==========================');
    } else {
      // Fallback to legacy payment fields if Payments list is not available
      console.log('=== USING LEGACY PAYMENT FIELDS ===');
      paymentData = {
        splitPaymentEnabled: false,
        selectedPaymentMethod: (() => {
          if (firstRecord.PaidbyCash > 0) return 'Cash';
          if (firstRecord.PaidbyOthers > 0) return 'Card';
          if (firstRecord.walletamount > 0) return 'Wallets';
          if (firstRecord.Chequeamount > 0) return 'Cheque';
          if (firstRecord.Couponamount > 0) return 'Coupon';
          if (firstRecord.CreditNoteamount > 0) return 'Credit Note';
          if (firstRecord.PaidbyPoints > 0) return 'Points';
          return 'Cash'; // Default
        })(),
        cashPayment: firstRecord.PaidbyCash?.toString() || '',
        walletProvider: 'PhonePe',
        walletAmount: firstRecord.walletamount?.toString() || '',
        walletTxNo: firstRecord.PaidDetails || '', // Map PaidDetails to walletTxNo for wallet transactions
        chequeAmount: firstRecord.Chequeamount?.toString() || '',
        chequeNo: '',
        couponNo: '',
        couponAmount: firstRecord.Couponamount?.toString() || '',
        creditNoteNo: '',
        creditNoteAmount: firstRecord.CreditNoteamount?.toString() || '',
        pointsRedeem: firstRecord.PaidbyPoints?.toString() || '',
        bankTransAmount: '', // Bank transfer field is not in GET API - would need separate field
        cardPayment: firstRecord.PaidbyOthers?.toString() || '', // PaidbyOthers maps to cardPayment for display
        payBefore: dueDate, // Use PaidOn as due date
        cashDenominationReceived: firstRecord.CashDenominationReceived?.toString() || '',
      };
    }
    
    setPayments(paymentData);

    // Set notes and rep name
    setNotes(firstRecord.Notes || '');
    setRepName(firstRecord.RepName || '');
    
    // Set shipping form data for "Ship to Same" functionality
    // Always populate shipping data if it exists, regardless of shipToSame state
    // This preserves the data so toggling checkbox won't lose it
    const hasShippingData = firstRecord.ShippingName || firstRecord.ShippingCompany || firstRecord.ShippingAddress || firstRecord.ShippingEmail || firstRecord.ShippingGST || firstRecord.ShippingPhone || firstRecord.shippingstate;
    
    console.log('=== SHIPPING DATA DEBUG ===');
    console.log('Has Shipping Data:', hasShippingData);
    console.log('ShippingName:', firstRecord.ShippingName);
    console.log('ShippingCompany:', firstRecord.ShippingCompany);
    console.log('ShippingAddress:', firstRecord.ShippingAddress);
    console.log('ShippingPhone:', firstRecord.ShippingPhone);
    console.log('ShippingEmail:', firstRecord.ShippingEmail);
    console.log('ShippingGST:', firstRecord.ShippingGST);
    console.log('shippingstate:', firstRecord.shippingstate);
    console.log('===========================');
    
    // Always set shipping form data to preserve it, even if empty
    // This ensures that when popup opens, the values are already there
    setShipToForm({
      Name: firstRecord.ShippingName || '',
      Company: firstRecord.ShippingCompany || '', // Use ShippingCompany field from API
      Phone: firstRecord.ShippingPhone || '',
      Email: firstRecord.ShippingEmail || '',
      GST: firstRecord.ShippingGST || '',
      Address: firstRecord.ShippingAddress || '',
      State: firstRecord.shippingstate || '',
    });
    
    // In edit mode, default to "Ship to Same" checked
    // The shipping data will be preserved regardless of the checkbox state
    setShipToSame(true);
    
    // Set invoice form fields with all available tags; Delivered: true so mobile Delivered Qty shows correct value
    setInvoiceForm({
      QuoteNo: firstRecord.Tag1 || '',
      Mode: firstRecord.Tag2 || '',
      VehicleNo: firstRecord.Tag3 || '',
      Tag4: firstRecord.Tag4 || '',
      InvoiceName: firstRecord.InvoiceName || '',
      InvSeqNo: firstRecord.InvSeqNo?.toString() || '',
      cashmemo: firstRecord.cashmemo?.toString() || '',
      CreatedBy: firstRecord.CreatedBy || '',
      Delivered: true,
    });
    
    console.log('Sales invoice context populated successfully with all fields');
  }, [setCustomer, setProducts, setPayments, setNotes, setRepName, setShipToForm, setShipToSame, setIsUnknown, setInvoiceForm, partners, locations]);

  const populateSalesInvoiceContext = useCallback((invoiceData: any) => {
    console.log('=== POPULATING SALES INVOICE CONTEXT (SINGLE PRODUCT) ===');
    console.log('Invoice data received:', invoiceData);
    console.log('Available partners:', partners);
    console.log('Available locations:', locations);
    console.log('=======================================================');
    
    // Detect if this is an Unknown customer
    const isUnknownCustomer = 
      (invoiceData.FName && invoiceData.FName.toUpperCase() === 'UNKNOWN') ||
      (invoiceData.LName && invoiceData.LName.toUpperCase() === 'UNKNOWN') ||
      (invoiceData.Name && invoiceData.Name.toUpperCase().includes('UNKNOWN'));
    
    console.log('=== UNKNOWN CUSTOMER DETECTION (SINGLE) ===');
    console.log('FName:', invoiceData.FName);
    console.log('LName:', invoiceData.LName);
    console.log('Name:', invoiceData.Name);
    console.log('Is Unknown Customer:', isUnknownCustomer);
    console.log('========================================');
    
    // Set Unknown checkbox state
    setIsUnknown(isUnknownCustomer);
    
    // Set customer data
    // Convert CreatedOn to YYYY-MM-DD format for InvoiceDate
    const invoiceDate = invoiceData.CreatedOn 
      ? new Date(invoiceData.CreatedOn).toISOString().slice(0, 10)
      : '';
    
    if (isUnknownCustomer) {
      // For Unknown customers, only set Name/Company to UNKNOWN and clear all other fields
      console.log('=== CLEARING UNKNOWN CUSTOMER FIELDS (Single Product) ===');
      console.log('Clearing phone, address, email, etc. for Unknown customer');
      setCustomer({
        Phone: '',
        Mobile1: '',
        Name: 'UNKNOWN',
        Company: 'UNKNOWN',
        GST: '',
        GSTN: '',
        Email: '',
        Address: '',
        Address1: '',
        State: invoiceData.CustState || '', // Keep state for business logic
        Locality: '',
        ReferredBy: '',
        SelectCompany: invoiceData.CompanyID?.toString() || '',
        Location: resolvedLocationId || (rawLocationStr ? '' : (invoiceData.Location || '')),
        Partner: '',
        eCommOrderID: '', // Clear eComm fields for Unknown
        InvoiceDate: invoiceDate,
        customerCode: invoiceData.CustomerCode || '', // Add customer code for points functionality
      });
      console.log('=== UNKNOWN CUSTOMER FIELDS CLEARED (Single Product) ===');
    } else {
      // For regular customers, populate all fields normally
      setCustomer({
        Phone: invoiceData.Mobile1 || '',
        Mobile1: invoiceData.Mobile1 || '',
        Name: invoiceData.FName || '',
        Company: invoiceData.LName || '',
        GST: invoiceData.TIN || '',
        GSTN: invoiceData.TIN || '',
        Email: invoiceData['Customer Email'] ?? invoiceData.emailid ?? '',
        Address: invoiceData.Address || '',
        Address1: invoiceData.Address1 || '',
        State: invoiceData.CustState || '',
        Locality: invoiceData.Locality || '',
        ReferredBy: invoiceData.ReferredDr || '',
        SelectCompany: invoiceData.CompanyID?.toString() || '',
        Location: resolvedLocationId || (rawLocationStr ? '' : (invoiceData.Location || '')),
        Partner: '',
        eCommOrderID: invoiceData.eCommOrder || '', // eCommOrder contains the eComm# textbox value
        InvoiceDate: invoiceDate, // CreatedOn converted to date string
        customerCode: invoiceData.CustomerCode || '', // Add customer code for points functionality
      });
    }

    // Set product data - convert single product to array format with proper structure
    // isIGST: 0 = intrastate (GST = SGST+CGST), 1 = interstate (GST = IGST only)
    const isIGSTSingle = invoiceData.isIGST === 1 || invoiceData.isIGST === true
      || !!(invoiceData.CustState && invoiceData.CmpState && invoiceData.CustState !== invoiceData.CmpState);
    // Calculate Offer Price as Total / Qty (not using API value directly)
    const total = invoiceData.TotalCost || 0;
    const qty = invoiceData.Quantity || 1;
    const calculatedOfferPrice = qty > 0 ? total / qty : 0;
    const discount = invoiceData.ldiscount || 0;

    // MRP: only from api/InvoiceFullDetails JSON — no formula, no fallback
    const mrpFromApi = (invoiceData.MRP !== undefined && invoiceData.MRP !== null && invoiceData.MRP !== '')
      ? parseFloat(invoiceData.MRP)
      : 0;
    const mrp = Number.isNaN(mrpFromApi) ? 0 : mrpFromApi;

    const calculatedDiscountPer = discount > 0 && mrp > 0 ? (100 * (mrp - calculatedOfferPrice) / mrp) : 0;

    // Use costprice from API (InvoiceFullDetails) - correct value; UnitCost may be taxable/unit price
    const costPrice = invoiceData.costprice ?? invoiceData.CostPrice ?? invoiceData.UnitCost ?? invoiceData.OriginalCost ?? 0;

    // GST/Cess: same formulas as POST so edit table and POST match
    const sgstAmtSingle = parseFloat(invoiceData.SGST ?? 0);
    const cgstAmtSingle = parseFloat(invoiceData.CGST ?? 0);
    const igstAmtSingle = parseFloat(invoiceData.IGST ?? 0);
    const cessAmtSingle = parseFloat(invoiceData.Cess ?? 0);
    const gstAmountFromApiSingle = isIGSTSingle ? igstAmtSingle : (sgstAmtSingle + cgstAmtSingle);
    let sgstValSingle = 0, cgstValSingle = 0;
    if (!isIGSTSingle && gstAmountFromApiSingle > 0) {
      const half = Math.floor((gstAmountFromApiSingle / 2) * 100) / 100;
      sgstValSingle = half;
      cgstValSingle = half;
    }

    const productItem = {
      ProdCode: invoiceData.ProductCode || '',
      code: invoiceData.ProductCode || '',
      Title: invoiceData.Title || '',
      Barcode: invoiceData.BarCode || '',
      HSN: invoiceData.HSNCode || '',
      MRP: mrp,
      Qty: invoiceData.Quantity || 0,
      DelQty: invoiceData.deliveredQty ?? invoiceData.Quantity ?? 0,
      Discount: invoiceData.ldiscount || 0,
      DiscountPer: calculatedDiscountPer.toFixed(2),
      SGST: sgstValSingle,
      CGST: cgstValSingle,
      IGST: invoiceData.IGST || 0,
      Cess: invoiceData.Cess || 0,
      CessAmount: cessAmtSingle,
      Total: invoiceData.TotalCost || 0,
      UnitCost: invoiceData.UnitCost || 0,
      UnitPrice: invoiceData.UnitCost || 0, // Use UnitCost as UnitPrice (pre-GST price)
      OriginalCost: invoiceData.OriginalCost || 0,
      CostPrice: costPrice,
      OriginalCostPrice: costPrice,
      available: invoiceData.available || 0,
      Available: invoiceData.available || 0,
      CollectionName: invoiceData.CollectionName || '',
      BrandName: invoiceData.BrandName || '',
      UQCName: invoiceData.UQCName || '',
      deliveredQty: invoiceData.deliveredQty ?? invoiceData.Quantity ?? 0,
      GST: (invoiceData.cgstper || 0) + (invoiceData.sgstper || 0) || (invoiceData.igstper || 0), // Use total GST percentage (CGST+SGST for intra-state, IGST for inter-state)
      CessPer: invoiceData.cesspercent ?? invoiceData.CessPer ?? 0,
      OfferPrice: calculatedOfferPrice, // Use calculated value instead of API value
      GSTAmount: gstAmountFromApiSingle.toFixed(2),
    };
    
    console.log('=== PRODUCT ITEM CREATED ===');
    console.log('Product item:', productItem);
    console.log('Setting products array with:', [productItem]);
    console.log('============================');
    
    setProducts([productItem]);

    // Set payment data
    // Fix date mapping: PaidOn is actually due date, not payment date
    // Fix timezone issue by parsing the date as UTC and formatting properly
    let dueDate = '';
    if (invoiceData.PaidOn && invoiceData.PaidOn !== '0001-01-01T00:00:00') {
      // Parse the date string directly without timezone conversion
      const datePart = invoiceData.PaidOn.split('T')[0]; // Get just the date part (YYYY-MM-DD)
      dueDate = datePart;
    }
    
    setPayments({
      splitPaymentEnabled: false,
      selectedPaymentMethod: (() => {
        if (invoiceData.PaidbyCash > 0) return 'Cash';
        if (invoiceData.PaidbyOthers > 0) return 'Card';
        if (invoiceData.walletamount > 0) return 'Wallets';
        if (invoiceData.Chequeamount > 0) return 'Cheque';
        if (invoiceData.Couponamount > 0) return 'Coupon';
        if (invoiceData.CreditNoteamount > 0) return 'Credit Note';
        if (invoiceData.PaidbyPoints > 0) return 'Points';
        return 'Cash'; // Default
      })(),
      cashPayment: invoiceData.PaidbyCash?.toString() || '',
      walletProvider: 'PhonePe',
      walletAmount: invoiceData.walletamount?.toString() || '',
      walletTxNo: invoiceData.PaidDetails || '', // Map PaidDetails to walletTxNo for wallet transactions
      chequeAmount: invoiceData.Chequeamount?.toString() || '',
      chequeNo: '',
      couponNo: '',
      couponAmount: invoiceData.Couponamount?.toString() || '',
      creditNoteNo: '',
      creditNoteAmount: invoiceData.CreditNoteamount?.toString() || '',
      pointsRedeem: invoiceData.PaidbyPoints?.toString() || '',
      bankTransAmount: '', // Bank transfer field is not in GET API - would need separate field
      cardPayment: invoiceData.PaidbyOthers?.toString() || '', // PaidbyOthers maps to cardPayment for display
      payBefore: dueDate, // Use PaidOn as due date
    });

    // Set notes and rep name
    setNotes(invoiceData.Notes || '');
    setRepName(invoiceData.RepName || '');
    
    // Set invoice form fields (Tag1, Tag2, Tag3); Delivered: true so mobile Delivered Qty shows correct value
    setInvoiceForm({
      QuoteNo: invoiceData.Tag1 || '',
      Mode: invoiceData.Tag2 || '',
      VehicleNo: invoiceData.Tag3 || '',
      Delivered: true,
    });
    
    // Set shipping form data for "Ship to Same" functionality
    // Always populate shipping data if it exists, regardless of shipToSame state
    // This preserves the data so toggling checkbox won't lose it
    const hasShippingData = invoiceData.ShippingName || invoiceData.ShippingCompany || invoiceData.ShippingAddress || invoiceData.ShippingEmail || invoiceData.ShippingGST || invoiceData.ShippingPhone || invoiceData.shippingstate;
    
    console.log('=== SHIPPING DATA DEBUG (Single Product) ===');
    console.log('Has Shipping Data:', hasShippingData);
    console.log('ShippingName:', invoiceData.ShippingName);
    console.log('ShippingCompany:', invoiceData.ShippingCompany);
    console.log('ShippingAddress:', invoiceData.ShippingAddress);
    console.log('ShippingPhone:', invoiceData.ShippingPhone);
    console.log('============================================');
    
    // Always set shipping form data to preserve it, even if empty
    // This ensures that when popup opens, the values are already there
    setShipToForm({
      Name: invoiceData.ShippingName || '',
      Company: invoiceData.ShippingCompany || '', // Use ShippingCompany field from API
      Phone: invoiceData.ShippingPhone || '',
      Email: invoiceData.ShippingEmail || '',
      GST: invoiceData.ShippingGST || '',
      Address: invoiceData.ShippingAddress || '',
      State: invoiceData.shippingstate || '',
    });
    
    // In edit mode, default to "Ship to Same" checked
    // The shipping data will be preserved regardless of the checkbox state
    setShipToSame(true);
    
    console.log('Sales invoice context populated successfully');
  }, [setCustomer, setProducts, setPayments, setNotes, setRepName, setShipToForm, setShipToSame, setIsUnknown]);
  
  // Load initial data
  useEffect(() => {
    fetchCompanies();
    fetchLocations();
    fetchUsers();
    fetchPartners();
  }, [fetchCompanies, fetchLocations, fetchUsers, fetchPartners]);

  // Initialize Bluetooth functionality for Android devices
  useEffect(() => {
    if (isAndroid && typeof window !== 'undefined' && !window.bluetoothSerial) {
      console.log('Bluetooth functionality not available - ensure bluetoothSerial plugin is installed');
    }
  }, [isAndroid]);
  
  // Load invoice report when filters change
  useEffect(() => {
    fetchInvoiceReport();
  }, [fetchInvoiceReport]);
  
  // Debug effect to monitor edit mode state changes
  useEffect(() => {
    console.log('=== EDIT MODE STATE CHANGE ===');
    console.log('isEditMode:', isEditMode);
    console.log('editingInvoice:', editingInvoice);
    console.log('context isEditMode:', contextIsEditMode);
    console.log('context editingInvoiceId:', contextEditingInvoiceId);
    console.log('================================');
  }, [isEditMode, editingInvoice, contextIsEditMode, contextEditingInvoiceId]);
  
  // Filter invoices based on search term, type, and sort by date in descending order
  const filteredInvoices = useMemo(() => {
    let filtered = invoices;
    
    // Apply search filter if search term exists
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(invoice => 
        invoice["Invoice No"].toString().includes(searchLower) ||
        invoice["Customer Name"].toLowerCase().includes(searchLower)
      );
    }
    
    // Apply type filter: Invoice / Credit Note (case-insensitive)
    if (filters.invoiceType !== 'all') {
      const typeMatch = filters.invoiceType.toLowerCase();
      filtered = filtered.filter(invoice => 
        (invoice.Type || '').trim().toLowerCase() === typeMatch
      );
    }
    
    // Sort by Invoice Date in descending order (newest first)
    return filtered.sort((a, b) => {
      const dateA = new Date(a["Invoice Date"]);
      const dateB = new Date(b["Invoice Date"]);
      return dateB.getTime() - dateA.getTime(); // Descending order
    });
  }, [invoices, filters.searchTerm, filters.invoiceType]);
  
  // Pagination for desktop and mobile display
  const paginatedInvoices = useMemo(() => {
    if (isMobile) {
      // For mobile, show first N invoices (for infinite scroll)
      return filteredInvoices.slice(0, mobileDisplayCount);
    } else {
      // For desktop, use regular pagination
      const startIndex = (currentPage - 1) * pageSize;
      return filteredInvoices.slice(startIndex, startIndex + pageSize);
    }
  }, [filteredInvoices, currentPage, pageSize, isMobile, mobileDisplayCount]);
  
  const totalPages = Math.ceil(filteredInvoices.length / pageSize);
  
  // Infinite scroll for mobile
  useEffect(() => {
    if (!isMobile || !observerRef.current) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && mobileDisplayCount < filteredInvoices.length) {
          setMobileDisplayCount(prev => Math.min(prev + 10, filteredInvoices.length));
        }
      },
      { threshold: 0.1 }
    );
    
    observer.observe(observerRef.current);
    
    return () => observer.disconnect();
  }, [isMobile, mobileDisplayCount, filteredInvoices.length]);
  
  // Reset mobile display count when filters change
  useEffect(() => {
    setMobileDisplayCount(10);
  }, [filters]);
  
  // Helper functions
  const getCompanyName = (companyId: number) => {
    const company = companies.find(c => c.Id === companyId);
    return company?.Name || 'Unknown Company';
  };
  
  const getLocationName = (locationId: number) => {
    const location = locations.find(l => parseInt(l.ID) === locationId);
    return location?.Name || 'Unknown Location';
  };
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  // Event handlers
  const handleFilterChange = (key: keyof FilterState, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };
  
  const handlePreview = async (invoice: InvoiceReport) => {
    setSelectedInvoice(invoice);
    // Use default template from Settings (Invoice Print Template) so preview matches configured default
    setSelectedTemplate(defaultPreviewTemplate);
    setShowPreviewModal(true);
    // The PDF preview will be automatically generated via useEffect when the modal opens
  };
  
  const handleEdit = async (invoice: InvoiceReport) => {
    console.log('=== STARTING EDIT OPERATION ===');
    console.log('Invoice to edit:', invoice);
    console.log('Invoice ID:', invoice.Invoiceid);
    console.log('Is Mobile:', isMobile);
    console.log('Will use component:', isMobile ? 'SalesInvoiceMobile' : 'SalesInvoice');
    console.log('================================');
    
    // Set edit mode first
    setIsEditMode(true);
    setEditingInvoice(invoice);
    
    try {
      // Fetch the full invoice details for editing
      const invoiceData = await fetchInvoiceDetailsForEdit(invoice.Invoiceid);
      
      console.log('=== EDIT OPERATION RESULTS ===');
      console.log('Invoice data fetched:', !!invoiceData);
      console.log('Invoice data details:', invoiceData);
      console.log('================================');
      
      if (invoiceData) {
        // Set edit mode and store the invoice being edited
        setEditingInvoice(invoice);
        setIsEditMode(true);
        
        // Set context for edit mode
        setContextEditMode(true);
        setEditingInvoiceId(invoice.Invoiceid);
        setOnBackToReports(() => handleBackFromEdit);
        setOnUpdateSuccess(() => handleInvoiceUpdateSuccess);
        
        console.log('=== CONTEXT SET FOR EDIT ===');
        console.log('Edit mode set to true');
        console.log('Editing invoice ID set to:', invoice.Invoiceid);
        console.log('Invoice ID type:', typeof invoice.Invoiceid);
        console.log('Invoice ID value:', invoice.Invoiceid);
        console.log('============================');
        
        toast({
          title: "Loading Invoice",
          description: `Loading invoice #${invoice["Invoice No"]} for editing...`,
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to load invoice data for editing",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error in handleEdit:', error);
      toast({
        title: "Error",
        description: "Failed to load invoice for editing",
        variant: "destructive",
      });
    }
  };
  
  // State for delete confirmation dialogs
  const [showFirstDeleteDialog, setShowFirstDeleteDialog] = useState(false);
  const [showSecondDeleteDialog, setShowSecondDeleteDialog] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState<InvoiceReport | null>(null);

  // Bluetooth printer state
  const [bluetoothDevices, setBluetoothDevices] = useState<any[]>([]);
  const [isScanningBluetooth, setIsScanningBluetooth] = useState(false);
  const [bluetoothScanError, setBluetoothScanError] = useState<string>('');
  const [selectedBluetoothDevice, setSelectedBluetoothDevice] = useState<string>('');
  const [isBluetoothConnected, setIsBluetoothConnected] = useState(false);
  const [connectedBluetoothDeviceName, setConnectedBluetoothDeviceName] = useState<string>('');
  const [showBluetoothModal, setShowBluetoothModal] = useState(false);
  const [isConnectingBluetooth, setIsConnectingBluetooth] = useState(false);
  const [isPrintingBluetooth, setIsPrintingBluetooth] = useState(false);

  // When Bluetooth modal is open, Android back button should close only the modal (Issue 2)
  const handleBackWithBluetoothModal = useCallback(() => {
    if (showBluetoothModal) {
      setShowBluetoothModal(false);
      return;
    }
    handleBackToReports();
  }, [showBluetoothModal, handleBackToReports]);

  useBackNavigation({
    onBack: handleBackWithBluetoothModal,
    canGoBack: true,
    showExitConfirmation: false,
  });

  const handleDelete = async (invoice: InvoiceReport) => {
    setInvoiceToDelete(invoice);
    setShowFirstDeleteDialog(true);
  };

  const handleFirstDeleteConfirm = () => {
    setShowFirstDeleteDialog(false);
    setShowSecondDeleteDialog(true);
  };

  const handleSecondDeleteConfirm = async () => {
    setShowSecondDeleteDialog(false);
    if (!invoiceToDelete) return;

    try {
      console.log('=== DELETING INVOICE ===');
      console.log('Invoice ID:', invoiceToDelete.Invoiceid);
      console.log('Invoice No:', invoiceToDelete["Invoice No"]);
      console.log('Base URL:', baseUrl);
      console.log('======================');
      
      // Call the delete API
      const response = await fetch(`${baseUrl}/api/Invoice?InvoiceID=${invoiceToDelete.Invoiceid}`, {
        method: 'DELETE',
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete invoice: ${response.status} ${response.statusText} - ${errorText}`);
      }
      
      // Show success message
      toast({
        title: "Success",
        description: "Invoice deleted successfully",
      });
      
      // Remove from local state
      setInvoices(prev => prev.filter(inv => inv.Invoiceid !== invoiceToDelete.Invoiceid));
      
      // Refresh the invoice list to get updated data
      fetchInvoiceReport();
      
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete invoice",
        variant: "destructive",
      });
    }
  };

  // Bluetooth printer functions
  const scanBluetoothDevices = async () => {
    console.log('=== SCAN BLUETOOTH DEVICES ===');
    console.log('Is Android:', isAndroid);
    console.log('User Agent:', navigator.userAgent);
    console.log('Bluetooth Available:', typeof window !== 'undefined' && window.bluetoothSerial);
    console.log('================================');
    
    if (!isAndroid) {
      toast({
        title: "Not Available",
        description: "Bluetooth printing is only available on Android devices",
        variant: "destructive",
      });
      return;
    }

    if (typeof window === 'undefined' || !window.bluetoothSerial) {
      toast({
        title: "Bluetooth Not Available",
        description: "Bluetooth functionality is not available. Please ensure the app is running in a Capacitor environment.",
        variant: "destructive",
      });
      return;
    }

    setIsScanningBluetooth(true);
    setBluetoothScanError('');
    setBluetoothDevices([]);

    try {
      // On Android, request location permission first (required for Bluetooth device discovery on Android 6+)
      if (typeof window !== 'undefined' && (window as any).cordova?.plugins?.permissions) {
        const permissions = (window as any).cordova.plugins.permissions;
        try {
          await new Promise<void>((resolve) => {
            permissions.requestPermission(permissions.ACCESS_FINE_LOCATION, (status: { hasPermission: boolean }) => {
              if (!status.hasPermission) {
                console.warn('Location permission denied. Bluetooth device discovery may not work.');
              }
              resolve();
            }, () => resolve());
          });
        } catch (e) {
          console.warn('Error requesting location permission:', e);
        }
      }

      // Check if Bluetooth is enabled
      const isEnabled = await checkBluetoothEnabled();
      if (!isEnabled) {
        // Try to enable Bluetooth
        try {
          await enableBluetooth();
        } catch (error) {
          throw new Error('Please enable Bluetooth in your device settings and try again.');
        }
      }

      // Scan for devices
      const devices = await listBluetoothDevices();
      setBluetoothDevices(devices);
      
      if (devices.length === 0) {
        setBluetoothScanError('No Bluetooth devices found. Please ensure your printer is turned on and in pairing mode.');
      }
    } catch (error) {
      console.error('Error scanning Bluetooth devices:', error);
      setBluetoothScanError(error instanceof Error ? error.message : 'Failed to scan for Bluetooth devices');
    } finally {
      setIsScanningBluetooth(false);
    }
  };

  const connectToBluetoothPrinter = async (deviceAddress: string) => {
    if (isConnectingBluetooth) return;
    setIsConnectingBluetooth(true);
    try {
      const device = bluetoothDevices.find(d => d.address === deviceAddress);
      await connectBluetoothDevice(deviceAddress, device?.name);
      setIsBluetoothConnected(true);
      setSelectedBluetoothDevice(deviceAddress);
      const deviceName = device?.name || 'Unknown Printer';
      setConnectedBluetoothDeviceName(deviceName);
      
      toast({
        title: "Connected Successfully",
        description: `Connected to ${deviceName}`,
      });
    } catch (error) {
      console.error('Error connecting to Bluetooth printer:', error);
      const userMessage = getPrinterErrorMessage(error);
      toast({
        title: "Connection Failed",
        description: userMessage,
        variant: "destructive",
      });
    } finally {
      setIsConnectingBluetooth(false);
    }
  };

  const disconnectFromBluetoothPrinter = async () => {
    try {
      await disconnectBluetoothDevice();
      setIsBluetoothConnected(false);
      setConnectedBluetoothDeviceName('');
      setSelectedBluetoothDevice('');
      
      toast({
        title: "Disconnected",
        description: "Disconnected from Bluetooth printer",
      });
    } catch (error) {
      console.error('Error disconnecting from Bluetooth printer:', error);
      toast({
        title: "Error",
        description: "Failed to disconnect from printer",
        variant: "destructive",
      });
    }
  };

  const printToBluetoothPrinter = async () => {
    if (!currentPdfDoc || !isBluetoothConnected) {
      toast({
        title: "Error",
        description: "No PDF available or Bluetooth printer not connected",
        variant: "destructive",
      });
      return;
    }
    if (isPrintingBluetooth) return; // Prevent duplicate clicks (Issue 3)
    setIsPrintingBluetooth(true);

    try {
      // For thermal prints (2mm and 3mm), we need to convert the PDF to ESC/POS commands
      if (selectedTemplate === '2mm-print' || selectedTemplate === '3mm-print') {
        // Get the invoice details for thermal printing
        if (!selectedInvoice) {
          throw new Error('No invoice selected for printing');
        }

        const detailsResponse = await fetch(`${baseUrl}/api/InvoiceFullDetails?InvoiceID=${selectedInvoice.Invoiceid}`, {
          headers: {
            'Authorization': authToken,
            'Content-Type': 'application/json',
          },
        });

        if (!detailsResponse.ok) {
          throw new Error('Failed to fetch invoice details for thermal printing');
        }

        const invoiceData = await detailsResponse.json();

        // Log full API response in JSON format (like Postman)
        console.log('=== FULL API RESPONSE (JSON) - InvoiceReportTab ===');
        console.log(JSON.stringify(invoiceData, null, 2));
        console.log('=== END API RESPONSE ===');
        console.log('InvoiceData Header:', invoiceData.Header);
        console.log('InvoiceData data[0] Header:', invoiceData.data && invoiceData.data[0]?.Header);

        // Import the thermal printing functions
        const { printKohineer, printKohineer2mm } = await import('@/lib/printKohineer');

        // Print based on template
        if (selectedTemplate === '2mm-print') {
          await printKohineer2mm({ 
            invoiceDetails: invoiceData, 
            companyImage: null 
          });
        } else {
          await printKohineer({ 
            invoiceDetails: invoiceData, 
            size: '3inch',
            companyImage: null,
            companyInfo: null,
            showSerialInPrint,
            serialPrintTemplate
          });
        }

        toast({
          title: "Print Sent",
          description: "Invoice sent to Bluetooth printer successfully",
        });
      } else {
        // For modern templates, we need to convert PDF to text/ESC/POS
        // This is a simplified approach - in a real implementation, you'd need a PDF to ESC/POS converter
        toast({
          title: "Not Supported",
          description: "Modern templates are not supported for Bluetooth printing. Use 2mm or 3mm templates.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error printing to Bluetooth printer:', error);
      toast({
        title: "Print Failed",
        description: error instanceof Error ? error.message : "Failed to print to Bluetooth printer",
        variant: "destructive",
      });
    } finally {
      setIsPrintingBluetooth(false);
    }
  };
  


  // Generate PDF and update preview
  const generatePreviewPDF = async () => {
    if (!selectedInvoice) return;
    
    setLoadingPreview(true);
    
    // Clear previous PDF URL
    setPdfUrl(null);
    
    try {
      // For 2mm and 3mm prints, use drawInvoicePdf function with invoice details
      if (selectedTemplate === '2mm-print' || selectedTemplate === '3mm-print') {
        console.log('=== GENERATING LOCAL PDF FOR THERMAL PRINT ===');
        console.log('Template:', selectedTemplate);
        console.log('Invoice ID:', selectedInvoice.Invoiceid);
        
        // Fetch invoice details with retry (helps with transient ERR_HTTP2_PROTOCOL_ERROR / Failed to fetch)
        const maxAttempts = 3;
        let lastError: Error | null = null;
        let detailsResponse: Response | null = null;
        const fetchUrl = `${baseUrl}/api/InvoiceFullDetails?InvoiceID=${selectedInvoice.Invoiceid}`;
        const fetchOpts = {
          headers: {
            'Authorization': authToken,
            'Content-Type': 'application/json',
          },
        };

        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
          try {
            detailsResponse = await fetch(fetchUrl, fetchOpts);
            break;
          } catch (err) {
            lastError = err instanceof Error ? err : new Error(String(err));
            if (attempt < maxAttempts) {
              console.warn(`InvoiceFullDetails fetch attempt ${attempt} failed, retrying...`, lastError.message);
              await new Promise((r) => setTimeout(r, 500 * attempt));
            }
          }
        }

        if (!detailsResponse) {
          throw lastError ?? new Error('Failed to fetch invoice details');
        }
        
        if (!detailsResponse.ok) {
          throw new Error('Failed to fetch invoice details for thermal printing');
        }
        
        const invoiceData = await detailsResponse.json();
        
        // Create PDF document based on template; height from content so no extra white space at bottom
        const paperWidth = selectedTemplate === '2mm-print' ? 58 : 80;
        const pageHeightMm = estimateInvoicePdfHeight(invoiceData, paperWidth);
        let doc: jsPDF;
        if (selectedTemplate === '2mm-print') {
          doc = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: [58, pageHeightMm]
          });
        } else {
          doc = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: [80, pageHeightMm]
          });
        }
        
        // Use drawInvoicePdf function for thermal prints
        await drawInvoicePdf(doc, invoiceData, paperWidth, 0, selectedInvoice.Invoiceid.toString(), { showSerialInPrint, serialPrintTemplate });
        
        // Store the PDF document for direct printing
        setCurrentPdfDoc(doc);
        
        // Create data URI for preview with proper PDF content type
        const pdfDataUri = doc.output('datauristring');
        // Ensure proper PDF content type is set
        setPdfUrl(pdfDataUri.replace('data:application/octet-stream', 'data:application/pdf'));
        
        console.log('Local thermal PDF generated successfully');
        
      } else {
        // For modern templates, use API
        console.log('=== USING API FOR MODERN TEMPLATES ===');
        
        let format: string;
        switch (selectedTemplate) {
          case 'single-page-modern':
            format = 'single page modern';
            break;
          case 'multiple-page-modern':
            format = 'multiple page modern';
            break;
          default:
            format = 'single page modern';
            break;
        }
        
        const apiEndpoint = `/api/GetInvoicePDF?InvoiceID=${selectedInvoice.Invoiceid}&format=${encodeURIComponent(format)}`;
        console.log('API Endpoint:', apiEndpoint);
        
        // Use the new fetchPDF utility with automatic token refresh
        const pdfBlob = await fetchPDF(apiEndpoint);
        
        // Convert blob to data URI to avoid iframe security issues
        // Convert blob to data URI using FileReader for better compatibility
        const reader = new FileReader();
        const pdfDataUriPromise = new Promise<string>((resolve, reject) => {
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
        });
        reader.readAsDataURL(pdfBlob);
        
        // Wait for FileReader to complete and set the data URI
        const pdfDataUri = await pdfDataUriPromise;
        setPdfUrl(pdfDataUri);
        setCurrentPdfDoc(pdfBlob as any);
        
        console.log('API PDF converted to data URI successfully');
      }
      
    } catch (error) {
      console.error('Error generating PDF preview:', error);
      const message = error instanceof Error ? error.message : String(error);
      const isNetworkError = message === 'Failed to fetch' || message.includes('HTTP2') || message.includes('NetworkError') || message.includes('Load failed');
      toast({
        title: "Error",
        description: isNetworkError
          ? "Network error loading PDF preview. Check your connection and try again."
          : `Failed to load PDF preview: ${message}`,
        variant: "destructive",
      });
    } finally {
      setLoadingPreview(false);
    }
  };

  // Function to open PDF using Capacitor PDF viewer
  const openPdfWithCapacitorViewer = async (pdfUrl: string, title: string = 'Invoice Preview') => {
    try {
      if (Capacitor.isNativePlatform() && PdfViewer) {
        // For Android/iOS, use Capacitor PDF viewer
        await PdfViewer.open({
          url: pdfUrl,
          title: title
        });
      } else if (Capacitor.isNativePlatform() && !PdfViewer) {
        // Fallback for native platforms without PDF viewer plugin
        toast({
          title: "PDF Viewer Not Available",
          description: "Native PDF viewer is not available. Please download the PDF to view it.",
          variant: "destructive",
        });
      } else {
        // For web, open in new tab
        window.open(pdfUrl, '_blank');
      }
    } catch (error) {
      console.error('Error opening PDF with Capacitor viewer:', error);
      toast({
        title: "Error",
        description: "Failed to open PDF viewer",
        variant: "destructive",
      });
    }
  };

  // Direct print function
  const handleDirectPrint = async () => {
    console.log('=== HANDLE DIRECT PRINT ===');
    console.log('Is Mobile:', isMobile);
    console.log('Is Android:', isAndroid);
    console.log('User Agent:', navigator.userAgent);
    console.log('Current PDF Doc:', !!currentPdfDoc);
    console.log('==========================');
    
    if (!currentPdfDoc) {
      toast({
        title: "Error",
        description: "No PDF available to print. Please generate the preview first.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Prepare PDF blob for both desktop and mobile
      let pdfBlob: Blob;
      
      // Ensure we have a proper Blob object
      if (currentPdfDoc instanceof Blob) {
        pdfBlob = currentPdfDoc;
      } else {
        pdfBlob = currentPdfDoc.output('blob');
      }

      // Convert data URI to blob if pdfUrl is a data URI
      if (pdfUrl && pdfUrl.startsWith('data:application/pdf')) {
        try {
          const base64Data = pdfUrl.split(',')[1];
          const binaryData = atob(base64Data);
          const bytes = new Uint8Array(binaryData.length);
          for (let i = 0; i < binaryData.length; i++) {
            bytes[i] = binaryData.charCodeAt(i);
          }
          pdfBlob = new Blob([bytes], { type: 'application/pdf' });
        } catch (e) {
          console.warn('Failed to convert data URI to blob, using existing blob');
        }
      }

      // Create blob URL
      const blobUrl = URL.createObjectURL(pdfBlob);

      // For large screens (non-mobile), open PDF in new tab with browser's native viewer
      if (!isMobile) {
        // Open PDF directly in new tab - browser will use its native PDF viewer
        const printWindow = window.open(blobUrl, '_blank');
        
        if (!printWindow) {
          toast({
            title: "Error",
            description: "Popup blocked. Please allow popups and try again.",
            variant: "destructive",
          });
          URL.revokeObjectURL(blobUrl);
          return;
        }

        // Wait for PDF to load, then trigger print
        setTimeout(() => {
          try {
            if (!printWindow.closed) {
              printWindow.focus();
              printWindow.print();
            }
          } catch (e) {
            console.error('Print trigger error:', e);
          }
        }, 1500);
        
        // Monitor when window closes and cleanup
        const checkClosed = setInterval(() => {
          if (printWindow.closed) {
            clearInterval(checkClosed);
            URL.revokeObjectURL(blobUrl);
          }
        }, 1000);
        
        // Fallback cleanup after 10 minutes
        setTimeout(() => {
          clearInterval(checkClosed);
          try {
            URL.revokeObjectURL(blobUrl);
          } catch (e) {}
        }, 600000);
        
        toast({
          title: "Print Dialog Opening",
          description: "PDF will open in a new tab with print dialog.",
          className: "bg-blue-600 text-white border-blue-700",
        });
        return;
      }

      // For native platforms (Android/iOS), show Bluetooth printer options if plugin available
      const isNativeWithBluetooth = Capacitor.isNativePlatform() && typeof window !== 'undefined' && (window as any).bluetoothSerial;
      if (isNativeWithBluetooth) {
        console.log('Native platform with Bluetooth detected, showing Bluetooth modal');
        
        // Check if Bluetooth is available
        if (typeof window === 'undefined' || !window.bluetoothSerial) {
          toast({
            title: "Bluetooth Not Available",
            description: "Bluetooth functionality is not available. Please ensure the app is running in a Capacitor environment.",
            variant: "destructive",
          });
          return;
        }
        
        setShowBluetoothModal(true);
        // Auto-scan for Bluetooth devices when modal opens
        if (bluetoothDevices.length === 0 && !isScanningBluetooth) {
          scanBluetoothDevices();
        }
      } else {
        // For mobile web browsers, open PDF directly to trigger native print dialog
        console.log('Mobile web browser detected, opening print dialog directly');
        
        // Open PDF directly in new tab - mobile browser will show its native PDF viewer and print option
        const printWindow = window.open(blobUrl, '_blank');
        
        if (!printWindow) {
          toast({
            title: "Error",
            description: "Popup blocked. Please allow popups and try again.",
            variant: "destructive",
          });
          URL.revokeObjectURL(blobUrl);
          return;
        }

        // Wait for PDF to load in mobile browser, then trigger print
        setTimeout(() => {
          try {
            if (!printWindow.closed) {
              printWindow.focus();
              printWindow.print();
            }
          } catch (e) {
            console.error('Mobile print trigger error:', e);
            // On some mobile browsers, window.print() might not work
            // In that case, user can use the browser's native print button
          }
        }, 1500);
        
        // Monitor when window closes and cleanup
        const checkClosed = setInterval(() => {
          if (printWindow.closed) {
            clearInterval(checkClosed);
            URL.revokeObjectURL(blobUrl);
          }
        }, 1000);
        
        // Fallback cleanup after 10 minutes
        setTimeout(() => {
          clearInterval(checkClosed);
          try {
            URL.revokeObjectURL(blobUrl);
          } catch (e) {}
        }, 600000);
        
        toast({
          title: "Print Dialog Opening",
          description: "PDF opened. Use your browser's print option or the print dialog.",
          className: "bg-blue-600 text-white border-blue-700",
        });
      }

    } catch (error) {
      console.error('Error printing PDF:', error);
      toast({
        title: "Error",
        description: "Failed to print PDF",
        variant: "destructive",
      });
    }
  };

  // Auto-generate preview when modal opens, template changes, or invoice is selected
  useEffect(() => {
    if (showPreviewModal && selectedInvoice) {
      console.log('Auto-generating PDF preview...');
      generatePreviewPDF();
    }
  }, [showPreviewModal, selectedTemplate, selectedInvoice]); // Trigger when invoice is selected or template changes

  // Cleanup PDF URL when component unmounts or modal closes
  useEffect(() => {
    if (!showPreviewModal) {
      // Clean up when modal closes
      if (pdfUrl && pdfUrl.startsWith('blob:')) {
        URL.revokeObjectURL(pdfUrl); // Clean up blob URLs to prevent memory leaks
      }
      // Note: Data URIs don't need cleanup like blob URLs
      setPdfUrl(null);
      setCurrentPdfDoc(null);
    }
  }, [showPreviewModal, pdfUrl]);

  // When modal closes: only clear scan state; keep connection so printer stays connected for the session until app is closed
  useEffect(() => {
      if (!showBluetoothModal) {
      setBluetoothScanError('');
      setIsScanningBluetooth(false);
      setIsConnectingBluetooth(false);
      setIsPrintingBluetooth(false);
      // Do NOT clear bluetoothDevices, selectedBluetoothDevice, isBluetoothConnected, connectedBluetoothDeviceName
      // so the selected printer stays connected for the whole invoice process until app is closed
    }
  }, [showBluetoothModal]);

  // Auto-connect when user selects a device from the list (same as invoice create flow)
  useEffect(() => {
    if (selectedBluetoothDevice && isAndroid && typeof window !== 'undefined' && (window as any).bluetoothSerial) {
      connectToBluetoothPrinter(selectedBluetoothDevice);
    }
  }, [selectedBluetoothDevice]);
  
  const resetFilters = () => {
    setFilters({
      searchTerm: '',
      startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)),
      endDate: new Date(),
      companyId: '0',
      locationId: '0',
      userName: showAllBranches ? 'all' : (users[0]?.UserID ?? 'all'),
      invoiceType: 'all',
    });
    setCurrentPage(1);
  };

  const handleBackFromEdit = async () => {
    console.log('=== HANDLE BACK FROM EDIT ===');
    console.log('Current isEditMode:', isEditMode);
    console.log('Current editingInvoice:', editingInvoice);
    
    // Reset local state first
    setIsEditMode(false);
    setEditingInvoice(null);
    setEditInvoiceData(null);
    
    // Reset context state - edit mode flags
    setContextEditMode(false);
    setEditingInvoiceId(null);
    setOnBackToReports(null);
    setOnUpdateSuccess(null);

    // Clear customer (GST, etc.), invoiceForm (QuoteNo, etc.), products, payments
    // so the next time Sales Invoice is opened for creation, fields are empty
    resetForNewInvoice();
    
    console.log('State reset completed');
    
    // Force a small delay to ensure state updates are processed
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Refresh the invoice report to show any updates
    await fetchInvoiceReport();
    
    // Show return message
    setShowReturnMessage(true);
    setTimeout(() => setShowReturnMessage(false), 3000);
    
    console.log('=== BACK FROM EDIT COMPLETED ===');
  };

  const handleInvoiceUpdateSuccess = async (message: string) => {
    console.log('=== INVOICE UPDATE SUCCESS ===');
    console.log('Success message:', message);
    
    toast({
      title: "Success",
      description: message,
    });
    
    // Go back to invoice reports after successful update
    await handleBackFromEdit();
  };
  
  // If in edit mode, show the appropriate sales invoice component
  console.log('=== RENDERING DECISION ===');
  console.log('isEditMode:', isEditMode);
  console.log('editingInvoice:', editingInvoice);
  console.log('Should show edit mode:', isEditMode && editingInvoice);
  
  if (isEditMode && editingInvoice) {
    console.log('Rendering SalesInvoice component');
    return (
      <div className="min-h-screen bg-white">
        {isMobile ? <SalesInvoiceMobile /> : <SalesInvoice />}
      </div>
    );
  }
  
  console.log('Rendering InvoiceReportTab UI');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-3 sm:p-6">
      {/* Mobile App Bar */}
      <div className="lg:hidden">
        <AppBar
          title="Invoice Report"
          showBackButton={true}
          onBack={handleBackToReports}
        />
      </div>
      {/* Return from Edit Message */}
      {showReturnMessage && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md">
          <div className="bg-green-100 border border-green-400 text-green-800 px-4 py-3 rounded relative shadow-lg flex items-center">
            <svg className="h-6 w-6 text-green-500 mr-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            <span className="block flex-1">Successfully returned to Invoice Report</span>
            <button
              type="button"
              onClick={() => setShowReturnMessage(false)}
              className="ml-4 text-green-700 hover:text-green-900"
            >
              ×
            </button>
          </div>
        </div>
      )}
      
      <div className={`max-w-7xl mx-auto ${isAndroid ? 'lg:mt-0 mt-8 space-y-2' : 'lg:mt-0 mt-14 space-y-6'}`}>
        {/* Sticky Header + Filters */}
        <div className={`sticky top-0 z-30 bg-gradient-to-br from-slate-50 to-slate-100 pt-1 sm:pt-2 ${isAndroid ? 'pb-0' : 'pb-2'}`}>
          <div className={`${isAndroid ? 'space-y-2' : 'space-y-6'}`}>
        {/* Header - Hidden on mobile since we have app bar */}
        <div className="hidden lg:flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-4">
            {/* Back Button */}
            <Button
              onClick={handleBackToReports}
              variant="ghost"
              size="sm"
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
                <FileText className="h-7 w-7 text-blue-600" />
                Invoice Report
              </h1>
              {/* Subtitle removed as per request */}
            </div>
          </div>
          
          
        </div>

        {/* Filters Card */}
        <Card className="shadow-lg border-0 bg-blue-50/80 backdrop-blur-sm">
          <CardHeader className="pb-4 lg:pb-4 pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2 lg:flex hidden">
                <Filter className="h-5 w-5 text-blue-600" />
                Filters
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsFilterExpanded(!isFilterExpanded)}
                className="p-0 h-9 w-9 lg:flex hidden"
                aria-label={isFilterExpanded ? 'Collapse filters' : 'Expand filters'}
                title={isFilterExpanded ? 'Collapse filters' : 'Expand filters'}
              >
                <ChevronDown className={cn(
                  "h-4 w-4 transition-transform",
                  isFilterExpanded ? "transform rotate-180" : ""
                )} />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6 lg:space-y-6 space-y-3">
            {/* Search Bar - Always Visible */}
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search invoice number or customer name..."
                  value={filters.searchTerm}
                  onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                  className="pl-10 pr-10 h-11 lg:h-11 h-9 bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                />
                {/* Clear button - only show when there's text */}
                {filters.searchTerm && (
                  <button
                    type="button"
                    onClick={() => handleFilterChange('searchTerm', '')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 hover:text-gray-600 focus:outline-none"
                    aria-label="Clear search"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              {/* Mobile Dropdown Arrow - Outside search bar */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsFilterExpanded(!isFilterExpanded)}
                className="p-2 h-11 lg:h-11 h-9 lg:hidden bg-white border border-gray-200 hover:bg-gray-50"
                aria-label={isFilterExpanded ? 'Collapse filters' : 'Expand filters'}
                title={isFilterExpanded ? 'Collapse filters' : 'Expand filters'}
              >
                <ChevronDown className={cn(
                  "h-4 w-4 transition-transform",
                  isFilterExpanded ? "transform rotate-180" : ""
                )} />
              </Button>
            </div>

            {/* Additional Filters - Collapsible on all devices */}
            <div className={cn(
              "transition-all duration-200",
              !isFilterExpanded ? "hidden" : ""
            )}>
              {/* Filter Grid - 2 Column Layout */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-6 gap-3">
              {/* Left Column - same structure as right for row alignment */}
              <div className="space-y-4 lg:space-y-4 space-y-2">
                {/* Start Date */}
                <div className="space-y-2 lg:space-y-2 space-y-1">
                  <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Start Date
                  </Label>
                  <CustomDatePicker
                    selectedDate={filters.startDate}
                    onChange={(date) => handleFilterChange('startDate', date)}
                    minYear={2020}
                    maxYear={new Date().getFullYear() + 1}
                  />
                </div>

                {/* Company Dropdown */}
                <div className="space-y-2 lg:space-y-2 space-y-1">
                  <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Company
                  </Label>
                  <Select
                    value={filters.companyId}
                    onValueChange={(value) => {
                      console.log('Company selected:', value);
                      handleFilterChange('companyId', value);
                    }}
                  >
                    <SelectTrigger className="h-10 lg:h-10 h-8 bg-white border-gray-200">
                      <SelectValue placeholder="All Companies" />
                    </SelectTrigger>
                    <SelectContent>
                      {showAllBranches && <SelectItem value="0">All Companies</SelectItem>}
                      {loadingCompanies ? (
                        <SelectItem value="loading" disabled>Loading...</SelectItem>
                      ) : companies.length > 0 ? (
                        companies.map((company) => (
                          <SelectItem key={company.Id} value={company.Id.toString()}>
                            {company.Name}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="no-data" disabled>No companies available</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* Type filter - Invoice / Credit Note (aligns with Users) */}
                <div className="space-y-2 lg:space-y-2 space-y-1">
                  <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Type
                  </Label>
                  <Select
                    value={filters.invoiceType}
                    onValueChange={(value: 'all' | 'Invoice' | 'Credit Note') => {
                      handleFilterChange('invoiceType', value);
                    }}
                  >
                    <SelectTrigger className="h-10 lg:h-10 h-8 bg-white border-gray-200">
                      <SelectValue placeholder="All (Invoice & Credit Note)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All (Invoice & Credit Note)</SelectItem>
                      <SelectItem value="Invoice">Invoice only</SelectItem>
                      <SelectItem value="Credit Note">Credit Note only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4 lg:space-y-4 space-y-2">
                {/* End Date */}
                <div className="space-y-2 lg:space-y-2 space-y-1">
                  <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    End Date
                  </Label>
                  <CustomDatePicker
                    selectedDate={filters.endDate}
                    onChange={(date) => handleFilterChange('endDate', date)}
                    minYear={2020}
                    maxYear={new Date().getFullYear() + 1}
                  />
                </div>

                {/* Location Dropdown */}
                <div className="space-y-2 lg:space-y-2 space-y-1">
                  <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Location
                  </Label>
                  <Select
                    value={filters.locationId}
                    onValueChange={(value) => {
                      console.log('Location selected:', value);
                      handleFilterChange('locationId', value);
                    }}
                  >
                    <SelectTrigger className="h-10 lg:h-10 h-8 bg-white border-gray-200">
                      <SelectValue placeholder="All Locations" />
                    </SelectTrigger>
                    <SelectContent>
                      {showAllBranches && <SelectItem value="0">All Locations</SelectItem>}
                      {loadingLocations ? (
                        <SelectItem value="loading" disabled>Loading...</SelectItem>
                      ) : locations.length > 0 ? (
                        locations.map((location) => (
                          <SelectItem key={location.ID} value={location.ID}>
                            {location.Name}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="no-data" disabled>No locations available</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* Users Dropdown */}
                <div className="space-y-2 lg:space-y-2 space-y-1">
                  <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Users
                  </Label>
                  <Select
                    value={filters.userName}
                    onValueChange={(value) => {
                      console.log('User selected:', value);
                      handleFilterChange('userName', value);
                    }}
                  >
                    <SelectTrigger className="h-10 lg:h-10 h-8 bg-white border-gray-200">
                      <SelectValue placeholder="All Users" />
                    </SelectTrigger>
                    <SelectContent>
                      {showAllBranches && <SelectItem value="all">All Users</SelectItem>}
                      {loadingUsers ? (
                        <SelectItem value="loading" disabled>Loading...</SelectItem>
                      ) : users.length > 0 ? (
                        users.map((user) => (
                          <SelectItem key={user.Id} value={user.UserID}>
                            {user.UserID}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="no-data" disabled>No users available</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Action Buttons removed as requested */}
            </div>
          </CardContent>
        </Card>
          </div>
        </div>

        {/* Results Summary */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Invoice Reports ({filteredInvoices.length} results)
            </h3>

          </div>
          {loadingInvoices && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <RefreshCw className="h-4 w-4 animate-spin" />
              Loading...
            </div>
          )}
        </div>

        {/* Invoice Display */}
        {isMobile ? (
          // Mobile Card Layout
          <div className="space-y-4 max-h-[calc(100vh-260px)] overflow-y-auto pr-1">
            {loadingInvoices ? (
              Array.from({ length: 5 }).map((_, index) => (
                <Card key={index} className="p-4">
                  <div className="space-y-3">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                </Card>
              ))
            ) : paginatedInvoices.length === 0 ? (
              <Card className="p-8 text-center">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No invoices found</h3>
                <p className="text-gray-500">Try adjusting your filters or search criteria</p>
              </Card>
            ) : (
              paginatedInvoices.map((invoice) => (
                <Card key={invoice.Invoiceid} className="bg-white shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-900">#{invoice["Invoice No"]}</h3>
                        <p className="text-sm text-gray-500">{formatDate(invoice["Invoice Date"])}</p>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handlePreview(invoice)}>
                            <Eye className="h-4 w-4 mr-2" />
                            Preview
                          </DropdownMenuItem>
                          {canEditInvoice && invoice.Type?.toLowerCase() !== 'credit note' && (
                            <DropdownMenuItem onClick={() => handleEdit(invoice)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                          )}
                          {canDeleteInvoice && (
                            <DropdownMenuItem onClick={() => handleDelete(invoice)}>
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Customer:</span>
                        <span className="text-sm font-medium">{invoice["Customer Name"]}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Company:</span>
                        <span className="text-sm">{invoice["Customer Company"]}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Phone:</span>
                        <span className="text-sm">{invoice["Customer Phone"]}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Amount:</span>
                        <span className="text-sm font-semibold text-green-600">
                          {formatCurrency(invoice.InvoicedAmount)}
                        </span>
                      </div>
                      {invoice["Total Pending"] > 0 && !isAndroid && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Pending:</span>
                          <Badge variant="secondary">{invoice["Total Pending"]}</Badge>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
            
            {/* Infinite Scroll Observer for Mobile */}
            {isMobile && paginatedInvoices.length > 0 && mobileDisplayCount < filteredInvoices.length && (
              <div ref={observerRef} className="flex justify-center py-4">
                <div className="text-center">
                  <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 text-blue-600" />
                  <p className="text-sm text-gray-600">Loading more invoices...</p>
                </div>
              </div>
            )}
            
            {/* Mobile End Message */}
            {isMobile && mobileDisplayCount >= filteredInvoices.length && filteredInvoices.length > 10 && (
              <div className="text-center py-4">
                <p className="text-sm text-gray-500">
                  Showing all {filteredInvoices.length} invoices
                </p>
              </div>
            )}
          </div>
        ) : (
          // Desktop Table Layout
          <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
            <CardContent className="p-0">
              <div className="overflow-x-auto max-h-[calc(100vh-260px)] overflow-y-auto pr-1">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold">CREATED ON</TableHead>
                      <TableHead className="font-semibold">INVOICE NO</TableHead>
                      <TableHead className="font-semibold">NAME</TableHead>
                      <TableHead className="font-semibold">COMPANY</TableHead>
                      <TableHead className="font-semibold">PHONE</TableHead>
                      <TableHead className="font-semibold">INVOICED AMOUNT</TableHead>
                      <TableHead className="font-semibold">ECOMM PARTNER</TableHead>
                      <TableHead className="font-semibold">ECOMM ORDER</TableHead>
                      <TableHead className="font-semibold">TYPE</TableHead>
                      <TableHead className="font-semibold">LOCATION</TableHead>
                      <TableHead className="font-semibold">PENDING DELIVERY</TableHead>
                      <TableHead className="font-semibold text-center">ACTIONS</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loadingInvoices ? (
                      Array.from({ length: 5 }).map((_, index) => (
                        <TableRow key={index}>
                          {Array.from({ length: 13 }).map((_, cellIndex) => (
                            <TableCell key={cellIndex}>
                              <Skeleton className="h-4 w-full" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : paginatedInvoices.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={13} className="text-center py-8">
                          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 mb-2">No invoices found</h3>
                          <p className="text-gray-500">Try adjusting your filters or search criteria</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedInvoices.map((invoice) => (
                        <TableRow key={invoice.Invoiceid} className="hover:bg-gray-50">
                          <TableCell>
                            {(() => {
                              try {
                                const date = new Date(invoice["Invoice Date"]);
                                const day = String(date.getDate()).padStart(2, '0');
                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                const year = date.getFullYear();
                                const hours = String(date.getHours()).padStart(2, '0');
                                const minutes = String(date.getMinutes()).padStart(2, '0');
                                return (
                                  <div className="text-sm">
                                    <div className="whitespace-nowrap">{`${day}-${month}-${year}`}</div>
                                    <div className="text-gray-500 whitespace-nowrap">{`${hours}:${minutes}`}</div>
                                  </div>
                                );
                              } catch (error) {
                                return invoice["Invoice Date"] || '-';
                              }
                            })()}
                          </TableCell>
                          <TableCell className="font-medium">#{invoice["Invoice No"]}</TableCell>
                          <TableCell>{invoice["Customer Name"]}</TableCell>
                          <TableCell>{invoice["Customer Company"]}</TableCell>
                          <TableCell>{invoice["Customer Phone"]}</TableCell>
                          <TableCell className="font-semibold text-green-600">
                            {formatCurrency(invoice.InvoicedAmount)}
                          </TableCell>
                          <TableCell>{invoice.eCommPartner || '-'}</TableCell>
                          <TableCell>{invoice.eCommOrder || '-'}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{invoice.Type}</Badge>
                          </TableCell>
                          <TableCell>{getLocationName(invoice.LocationId)}</TableCell>
                          <TableCell>
                            {!isAndroid && invoice["Total Pending"] > 0 ? (
                              <Badge variant="secondary">{invoice["Total Pending"]}</Badge>
                            ) : (
                              <Badge variant="default" className="bg-green-100 text-green-800">Complete</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handlePreview(invoice)}
                                className="h-8 w-8 p-0 hover:bg-blue-100"
                              >
                                <Eye className="h-4 w-4 text-blue-600" />
                              </Button>
                              {canEditInvoice && invoice.Type?.toLowerCase() !== 'credit note' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEdit(invoice)}
                                  className="h-8 w-8 p-0 hover:bg-amber-100"
                                >
                                  <Edit className="h-4 w-4 text-amber-600" />
                                </Button>
                              )}
                              {canDeleteInvoice && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(invoice)}
                                  className="h-8 w-8 p-0 hover:bg-red-100"
                                >
                                  <Trash2 className="h-4 w-4 text-red-600" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pagination - Desktop Only */}
        {!isMobile && totalPages > 1 && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
            <div className="text-sm text-gray-500">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, filteredInvoices.length)} of {filteredInvoices.length} entries
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialogs */}
      <AlertDialog open={showFirstDeleteDialog} onOpenChange={setShowFirstDeleteDialog}>
        <AlertDialogContent className="bg-red-600 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Invoice</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-100">
              Are you sure you want to delete this invoice?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="bg-red-600">
            <AlertDialogCancel className="bg-white text-red-600 hover:bg-gray-100">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleFirstDeleteConfirm}
              className="bg-white text-red-600 hover:bg-gray-100"
            >
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showSecondDeleteDialog} onOpenChange={setShowSecondDeleteDialog}>
        <AlertDialogContent className="bg-red-600 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Delete</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-100">
              The Invoice sequence numbers will be adjusted, Payment reference number will not be changed.<br/>
              Inventory adjustments will be done.<br/><br/>
              Are you sure you really want to delete the selected Invoice?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="bg-red-600">
            <AlertDialogCancel className="bg-white text-red-600 hover:bg-gray-100">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleSecondDeleteConfirm}
              className="bg-white text-red-600 hover:bg-gray-100"
            >
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-6xl w-[95vw] max-h-[95vh] overflow-hidden p-0 keyboard-aware-container">
          <DialogHeader className="p-6 pb-4">
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Invoice Preview - #{selectedInvoice?.["Invoice No"]}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex flex-col h-full">
            {/* Template Selector and Controls */}
            <div className="px-6 pb-4 border-b">
              <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Template</Label>
                    <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                      <SelectTrigger className="w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="multiple-page-modern">Multiple Page Modern</SelectItem>
                        <SelectItem value="2mm-print">2mm Print</SelectItem>
                        <SelectItem value="3mm-print">3mm Print</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      onClick={generatePreviewPDF} 
                      className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                      disabled={loadingPreview}
                    >
                      <Eye className="h-4 w-4" />
                      {loadingPreview ? 'Generating...' : 'Generate PDF'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Dynamic Preview Area */}
            <div className="flex-1 p-4 sm:p-6 overflow-y-auto">
              {loadingPreview ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2 text-blue-600" />
                    <p className="text-gray-600">Generating preview...</p>
                  </div>
                </div>
              ) : selectedInvoice ? (
                <div className="h-full flex flex-col">
                                        {/* PDF Preview Container */}
                     <div className="flex-1 bg-gray-100 rounded-lg border overflow-hidden relative min-h-[400px] max-h-[70vh]">
                     {/* Loading state */}
                     {!pdfUrl && (
                       <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
                         <div className="text-center">
                           <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2 text-blue-600" />
                           <p className="text-gray-600">Generating PDF preview...</p>
                         </div>
                       </div>
                     )}
                     
                     {/* PDF preview container with improved mobile support */}
                     <div className="w-full h-full flex flex-col">
                       {/* Main content area with PDF viewer */}
                       <div className="flex-1 relative min-h-[500px] mb-16">
                         {/* Loading state */}
                         {loadingPreview && (
                           <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-20">
                             <div className="text-center">
                               <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2 text-blue-600" />
                               <p className="text-gray-600">Generating preview...</p>
                             </div>
                           </div>
                         )}

                         {/* PDF viewer */}
                         {pdfUrl && (
                           <div className="w-full h-full">
                             <PdfPreviewer fileUrl={pdfUrl} />
                           </div>
                         )}
                       </div>


                     </div>
                     

                     

                   </div>
                  
                  {/* Bottom Action Buttons */}
                  {pdfUrl && (
                    <div className="mt-4 flex gap-3 justify-center flex-wrap">
                      {/* Open in Native PDF Viewer (iOS only - hidden on Android) */}
                      {Capacitor.isNativePlatform() && Capacitor.getPlatform() !== 'android' && (
                        <Button
                          onClick={() => openPdfWithCapacitorViewer(pdfUrl, `Invoice #${selectedInvoice?.["Invoice No"]}`)}
                          className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-2"
                        >
                          <Eye className="h-4 w-4" />
                          Open PDF
                        </Button>
                      )}
                      
                      <Button
                        onClick={() => {
                          const fileName = `Invoice-${selectedInvoice?.["Invoice No"]}`;
                          downloadPdf(
                            pdfUrl,
                            fileName,
                            () => {
                              toast({
                                title: "Download Success",
                                description: Capacitor.isNativePlatform() 
                                  ? "PDF saved to device" 
                                  : "PDF downloaded successfully",
                                className: 'bg-green-600 text-white border-green-700'
                              });
                            },
                            (error) => {
                              toast({
                                title: "Download Error",
                                description: error,
                                variant: "destructive"
                              });
                            }
                          );
                        }}
                        className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-2"
                      >
                        <Download className="h-4 w-4" />
                        Download
                      </Button>
                      

                      
                      <Button
                        onClick={handleDirectPrint}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2"
                      >
                        <Printer className="h-4 w-4" />
                        Print
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No invoice data available</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bluetooth Printer Modal */}
      <Dialog open={showBluetoothModal} onOpenChange={setShowBluetoothModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="h-5 w-5" />
              Bluetooth Printer
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Connection Status */}
            {isConnectingBluetooth && (
              <div className="flex items-center gap-2 text-blue-600 text-sm bg-blue-50 p-3 rounded border">
                <RefreshCw className="h-4 w-4 animate-spin flex-shrink-0" />
                <span>Connecting...</span>
              </div>
            )}
            {isBluetoothConnected && connectedBluetoothDeviceName && !isConnectingBluetooth && (
              <div className="flex items-center gap-2 text-green-600 text-sm bg-green-50 p-3 rounded border">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <span>✓ Connected to: <strong>{connectedBluetoothDeviceName}</strong></span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={disconnectFromBluetoothPrinter}
                  className="ml-auto"
                >
                  Disconnect
                </Button>
              </div>
            )}

            {/* Scan Button */}
            {!isBluetoothConnected && !isConnectingBluetooth && (
              <div className="space-y-2">
                <Button
                  onClick={scanBluetoothDevices}
                  disabled={isScanningBluetooth}
                  className="w-full"
                >
                  {isScanningBluetooth ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                      Scanning...
                    </>
                  ) : (
                    <>
                      <Search className="h-4 w-4 mr-2" />
                      Scan for Bluetooth Printers
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Scan Error */}
            {bluetoothScanError && (
              <div className="text-red-600 text-sm bg-red-50 p-3 rounded border">
                <p className="font-medium">Scan Error:</p>
                <p>{bluetoothScanError}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={scanBluetoothDevices}
                  className="mt-2"
                >
                  Retry Scan
                </Button>
              </div>
            )}

            {/* Device List */}
            {bluetoothDevices.length > 0 && !isBluetoothConnected && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Available Printers:</Label>
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {bluetoothDevices.map((device) => (
                    <div
                      key={device.address}
                      className={`p-3 border rounded-lg transition-colors ${
                        isConnectingBluetooth
                          ? 'opacity-60 pointer-events-none'
                          : 'cursor-pointer hover:border-gray-300'
                      } ${
                        selectedBluetoothDevice === device.address
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200'
                      }`}
                      onClick={() => !isConnectingBluetooth && setSelectedBluetoothDevice(device.address)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-sm">
                            {formatDeviceName(device)}
                            {isPrinterDevice(device) && ' 🖨️'}
                          </p>
                          <p className="text-xs text-gray-500">{device.address}</p>
                        </div>
                        {selectedBluetoothDevice === device.address && (
                          <span className="text-blue-600">✓</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Connect Button */}
            {selectedBluetoothDevice && !isBluetoothConnected && (
              <Button
                onClick={() => connectToBluetoothPrinter(selectedBluetoothDevice)}
                disabled={isConnectingBluetooth}
                className="w-full"
              >
                {isConnectingBluetooth ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                    Connecting...
                  </>
                ) : (
                  'Connect to Selected Printer'
                )}
              </Button>
            )}

            {/* Print Button */}
            {isBluetoothConnected && (
              <div className="space-y-2">
                <Button
                  onClick={printToBluetoothPrinter}
                  disabled={isPrintingBluetooth}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  {isPrintingBluetooth ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                      Printing...
                    </>
                  ) : (
                    <>
                      <Printer className="h-4 w-4 mr-2" />
                      Print to Bluetooth Printer
                    </>
                  )}
                </Button>
                <p className="text-xs text-gray-500 text-center">
                  Note: Only 2mm and 3mm templates are supported for Bluetooth printing
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setShowBluetoothModal(false)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoiceReportTab;